import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Card, Badge, Empty, Skeleton, ErrorBox, ReviewBanner,
} from "@/components/ui";
import type {
  AIEntry, AIReviewResponse, ActivityRow, Alert, Appointment,
  CaseManagerMessage, Note, Patient, PatientLab, PatientMedia,
  PatientOverview, ReportAttachment, Task, TimelineEvent, NextVisitNote,
} from "@/types";

const TABS = [
  "Command Center", "Clinical Data", "Clinical Chronology", "Notes", "Tasks",
  "Appointments", "Reports", "Media/Labs", "Correction Mode", "RPIS Agent", "Activity",
] as const;
type Tab = typeof TABS[number];
type Tone = "ok" | "warn" | "bad" | "info" | "muted";

type AIMetadata = {
  author_type?: string;
  model?: string;
  role?: string;
  review_status?: string;
  source_hash?: string;
  source_ref?: string;
  confidence?: unknown;
  editable?: boolean;
  reversible?: boolean;
};

const APPLY_KEYS = [
  "source_backed_facts", "timeline", "draft_note", "tasks",
  "appointments", "contradictions",
];

export default function PatientFile() {
  const { id = "" } = useParams();
  const [tab, setTab] = useState<Tab>("Command Center");

  const overview = useQuery({
    queryKey: ["overview", id],
    queryFn: () => api.get<PatientOverview>(`/patients/${id}/overview`),
    enabled: !!id,
  });

  if (!id) return <Empty title="No patient id" />;
  if (overview.isLoading) return <Skeleton rows={8} />;
  if (overview.error) return <ErrorBox error={overview.error} />;
  if (!overview.data) return <Empty title="Patient not found." />;

  const p: Patient = overview.data.patient;
  const ov = overview.data;

  return (
      <div className="space-y-4">
      <PatientHeader p={p} ov={ov} />
      <AdvancedActions patientId={id} />

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? "tab-active" : "tab"}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Command Center" && <CommandCenterTab id={id} ov={ov} />}
      {tab === "Clinical Data" && <ClinicalDataTab id={id} />}
      {tab === "Clinical Chronology" && <TimelineTab id={id} />}
      {tab === "Notes" && <NotesTab id={id} />}
      {tab === "Tasks" && <TasksTab id={id} />}
      {tab === "Appointments" && <AppointmentsTab id={id} />}
      {tab === "Reports" && <ReportsTab id={id} />}
      {tab === "Media/Labs" && <MediaLabsTab id={id} />}
      {tab === "Correction Mode" && <CorrectionModeTab id={id} />}
      {tab === "RPIS Agent" && <AgentTab id={id} />}
      {tab === "Activity" && <ActivityTab id={id} />}
    </div>
  );
}

function PatientHeader({ p, ov }: { p: Patient; ov: PatientOverview }) {
  const followUpTone = normalizeTone(ov.follow_up_status_tone);
  const isStableFollowUp = String(ov.follow_up_status || "").startsWith("complete");
  const needsUpdate = Boolean(ov.follow_up_update_missing) || (!isStableFollowUp && (!ov.last_clinical_update_at || !p.next_action_text));
  const cardClass = ov.follow_up_update_missing
    ? "border-amber-700/80 bg-amber-950/20"
    : isStableFollowUp
    ? "border-emerald-800/70 bg-emerald-950/15"
    : "border-cyan-900/60 bg-slate-950/80";
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const clinical = ov.clinical_data || {};
  const header = patientHeaderSummary(p, clinical);
  const rt = radiotherapyDisplay(p, clinical);
  const archive = useMutation({
    mutationFn: () => api.post(`/patients/${p.id}/archive`, { reason: archiveReason, confirm_text: confirmText }),
    onSuccess: () => {
      setArchiveOpen(false);
      setArchiveReason("");
      setConfirmText("");
      qc.invalidateQueries({ queryKey: ["overview", p.id] });
    },
  });
  const restore = useMutation({
    mutationFn: () => api.post(`/patients/${p.id}/restore`, { reason: "Restored from patient command center" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["overview", p.id] }),
  });
  const hardDelete = useMutation({
    mutationFn: () => api.delete(`/patients/${p.id}`, { reason: deleteReason, confirm_text: deleteConfirmText }),
    onSuccess: () => navigate("/registry"),
  });
  return (
    <Card className={cardClass}>
      {p.is_archived ? (
        <div className="mb-3 review-banner">
          Archived patient is hidden from active lists but remains auditable. Reason: {p.archive_reason || "not recorded"}
        </div>
      ) : null}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold leading-tight text-slate-50">{p.full_name}</h1>
            <Badge tone="muted">MRN {p.mrn}</Badge>
            <Badge tone="info">{header.group}</Badge>
            <Badge tone={p.urgency === "critical" ? "bad" : p.urgency === "urgent" ? "warn" : "muted"}>
              {titleCase(p.urgency || "routine")}
            </Badge>
          </div>
          <div className="mt-1 text-sm text-slate-400">
            MRN {p.mrn} · {normalizedSexShort(p.sex)} · {ageLabel(p.date_of_birth)} · {header.group} · {titleCase(p.urgency || "routine")}
          </div>

          <div className="mt-4 grid gap-2 text-sm">
            <ClinicalHeaderRow label="Dx" value={header.diagnosis} />
            <ClinicalHeaderRow label="ICD-10" value={header.icd10} />
            <ClinicalHeaderRow label="Stage" value={header.stage} />
            {header.postOp ? <ClinicalHeaderRow label="Post-op" value={header.postOp} /> : null}
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">RT</span>
              {rt.statusBadge ? <Badge tone={rt.statusTone}>{rt.statusBadge}</Badge> : null}
              <Badge tone={rt.historyTone}>{rt.historyBadge}</Badge>
            </div>
            {header.plan ? <ClinicalHeaderRow label="Plan" value={header.plan} /> : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap justify-start gap-2 xl:justify-end">
          <button className="btn-primary !px-2.5 !py-1 text-xs" type="button" onClick={() => setEditing(true)}>Edit</button>
          {p.is_archived ? (
            <button className="btn-ghost !px-2.5 !py-1 text-xs" type="button" disabled={restore.isPending} onClick={() => restore.mutate()}>
              {restore.isPending ? "Restoring..." : "Restore"}
            </button>
          ) : (
            <button className="btn-ghost !px-2.5 !py-1 text-xs" type="button" onClick={() => setArchiveOpen((v) => !v)}>Archive</button>
          )}
          <button className="btn-danger !bg-red-950/70 !px-2.5 !py-1 text-xs !text-red-100 hover:!bg-red-900" type="button" onClick={() => setDeleteOpen((v) => !v)}>Delete</button>
        </div>
      </div>

      {archiveOpen ? (
        <div className="mt-3 card-tight space-y-2">
          <div className="text-sm text-slate-300">Archived patient is hidden from active lists but remains auditable.</div>
          <textarea className="input min-h-[80px]" value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} placeholder="Archive reason" />
          <input className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type ARCHIVE" />
          <div className="flex gap-2">
            <button className="btn-danger" disabled={!archiveReason || confirmText !== "ARCHIVE" || archive.isPending} onClick={() => archive.mutate()}>
              {archive.isPending ? "Archiving..." : "Confirm archive"}
            </button>
            <button className="btn-ghost" onClick={() => setArchiveOpen(false)}>Cancel</button>
          </div>
          {archive.error ? <ErrorBox error={archive.error} /> : null}
        </div>
      ) : null}
      {deleteOpen ? (
        <div className="mt-3 card-tight space-y-2 border border-red-900/70">
          <div className="text-sm text-red-200">Permanent delete removes this patient and dependent records so the same MRN can be re-entered cleanly. This is for test records or wrong patients.</div>
          <textarea className="input min-h-[80px]" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Permanent delete reason" />
          <input className="input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE PATIENT" />
          <div className="flex gap-2">
            <button className="btn-danger" disabled={!deleteReason || deleteConfirmText !== "DELETE PATIENT" || hardDelete.isPending} onClick={() => hardDelete.mutate()}>
              {hardDelete.isPending ? "Deleting..." : "Confirm permanent delete"}
            </button>
            <button className="btn-ghost" onClick={() => setDeleteOpen(false)}>Cancel</button>
          </div>
          {hardDelete.error ? <ErrorBox error={hardDelete.error} /> : null}
        </div>
      ) : null}
      {restore.error ? <div className="mt-3"><ErrorBox error={restore.error} /></div> : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line/70 pt-3 text-xs text-slate-500">
        <span>Tasks: <span className={Number(ov.open_tasks_count || 0) ? "text-sky-200" : "text-slate-400"}>{ov.open_tasks_count ?? 0} open</span></span>
        <span>· {ov.overdue_tasks_count ?? 0} overdue</span>
        <span>· {ov.upcoming_appointments_count ?? 0} upcoming</span>
        <span>· Reports: {ov.unreviewed_reports_count ?? 0}</span>
        <span>· Last review: {formatDateTime(p.last_physician_seen_at || ov.last_clinical_update_at)}</span>
        {ov.follow_up_status_label ? (
          <Badge tone={followUpTone}>{ov.follow_up_status_label}</Badge>
        ) : needsUpdate ? (
          <Badge tone="warn">Needs status update</Badge>
        ) : (
          <Badge tone="info">{p.status_label || "Current"}</Badge>
        )}
      </div>
      {p.next_action_text ? <div className="mt-2 text-xs text-slate-400">Next action: {p.next_action_text}</div> : null}
      {ov.follow_up_reminder ? (
        <div className={ov.follow_up_update_missing ? "mt-3 review-banner" : "mt-3 rounded border border-emerald-900/60 bg-emerald-950/20 p-3 text-sm text-emerald-100"}>
          {ov.follow_up_reminder}
          {ov.registered_follow_up_date ? <span className="block text-xs opacity-80">Registered follow-up: {formatDateTime(ov.registered_follow_up_date)}</span> : null}
        </div>
      ) : needsUpdate ? (
        <div className="mt-3 review-banner">
          Required action: add clinical update, next action, appointment/test, task, or close the alert with a reason.
        </div>
      ) : null}
      {editing ? <PatientEditPanel patient={p} onClose={() => setEditing(false)} /> : null}
    </Card>
  );
}

function PatientEditPanel({ patient, onClose }: { patient: Patient; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    mrn: patient.mrn || "",
    full_name: patient.full_name || "",
    sex: patient.sex || "",
    date_of_birth: patient.date_of_birth || "",
    primary_diagnosis: patient.primary_diagnosis || "",
    icd10: patient.icd10 || "",
    diagnosis_group: patient.diagnosis_group || "",
    stage_summary: patient.stage_summary || "",
    c_tnm: patient.c_tnm || "",
    p_tnm: patient.p_tnm || "",
    yp_tnm: patient.yp_tnm || "",
    metastatic_site_codes: patient.metastatic_site_codes || "",
    stage_group: patient.stage_group || "",
    treatment_phase: patient.treatment_phase || "workup",
    status_label: patient.status_label || "",
    urgency: patient.urgency || "routine",
    next_action_text: patient.next_action_text || "",
    next_action_due: patient.next_action_due || "",
    last_physician_seen_at: toLocalDatetimeValue(patient.last_physician_seen_at),
    follow_up_interval: patient.follow_up_interval || "",
    hr_status: patient.hr_status || "",
    her2_status: patient.her2_status || "",
    rt_status: patient.rt_status || "",
    rt_dose: patient.rt_dose || "",
    rt_fractions: patient.rt_fractions || "",
    rt_date: patient.rt_date || "",
    assigned_physician_id: patient.assigned_physician_id || "",
  } as any);
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/${patient.id}`, {
      ...blankToNull(form),
      last_physician_seen_at: form.last_physician_seen_at ? new Date(form.last_physician_seen_at).toISOString() : null,
      rt_fractions: form.rt_fractions === "" ? null : Number(form.rt_fractions),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["overview", patient.id] });
      onClose();
    },
  });
  return (
    <div className="mt-4 card-tight space-y-3">
      <div className="grid md:grid-cols-3 gap-2">
        <EditInput label="MRN" value={form.mrn} onChange={(v) => setForm({ ...form, mrn: v })} />
        <EditInput label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <EditInput label="Sex" value={form.sex} onChange={(v) => setForm({ ...form, sex: v })} />
        <EditInput label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
        <EditInput label="Primary diagnosis" value={form.primary_diagnosis} onChange={(v) => setForm({ ...form, primary_diagnosis: v })} />
        <EditInput label="ICD10" value={form.icd10} onChange={(v) => setForm({ ...form, icd10: v })} />
        <EditInput label="Diagnosis group" value={form.diagnosis_group} onChange={(v) => setForm({ ...form, diagnosis_group: v })} />
        <EditInput label="Stage summary" value={form.stage_summary} onChange={(v) => setForm({ ...form, stage_summary: v })} />
        <EditInput label="cTNM" value={form.c_tnm} onChange={(v) => setForm({ ...form, c_tnm: v })} />
        <EditInput label="pTNM" value={form.p_tnm} onChange={(v) => setForm({ ...form, p_tnm: v })} />
        <EditInput label="ypTNM" value={form.yp_tnm} onChange={(v) => setForm({ ...form, yp_tnm: v })} />
        <EditInput label="M-site codes" value={form.metastatic_site_codes} onChange={(v) => setForm({ ...form, metastatic_site_codes: v })} />
        <EditInput label="Stage group" value={form.stage_group} onChange={(v) => setForm({ ...form, stage_group: v })} />
        <EditInput label="Treatment phase" value={form.treatment_phase} onChange={(v) => setForm({ ...form, treatment_phase: v })} />
        <EditInput label="Current status" value={form.status_label} onChange={(v) => setForm({ ...form, status_label: v })} />
        <EditInput label="Urgency" value={form.urgency} onChange={(v) => setForm({ ...form, urgency: v })} />
        <EditInput label="Next action due" type="date" value={form.next_action_due} onChange={(v) => setForm({ ...form, next_action_due: v })} />
        <EditInput label="Last physician review" type="datetime-local" value={form.last_physician_seen_at} onChange={(v) => setForm({ ...form, last_physician_seen_at: v })} />
        <EditInput label="Follow-up interval" value={form.follow_up_interval} onChange={(v) => setForm({ ...form, follow_up_interval: v })} />
        <EditInput label="HR status" value={form.hr_status} onChange={(v) => setForm({ ...form, hr_status: v })} />
        <EditInput label="HER2 status" value={form.her2_status} onChange={(v) => setForm({ ...form, her2_status: v })} />
        <EditInput label="RT status" value={form.rt_status} onChange={(v) => setForm({ ...form, rt_status: v })} />
        <EditInput label="RT dose" value={form.rt_dose} onChange={(v) => setForm({ ...form, rt_dose: v })} />
        <EditInput label="RT fractions" type="number" value={String(form.rt_fractions || "")} onChange={(v) => setForm({ ...form, rt_fractions: v })} />
        <EditInput label="RT date" type="date" value={form.rt_date} onChange={(v) => setForm({ ...form, rt_date: v })} />
        <EditInput label="Responsible physician id" value={form.assigned_physician_id} onChange={(v) => setForm({ ...form, assigned_physician_id: v })} />
      </div>
      <div><label className="label">Next action</label><textarea className="input min-h-[90px]" value={form.next_action_text} onChange={(e) => setForm({ ...form, next_action_text: e.target.value })} /></div>
      <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving..." : "Save patient details"}</button><button className="btn-ghost" onClick={onClose}>Cancel</button></div>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </div>
  );
}

function AdvancedActions({ patientId }: { patientId: string }) {
  return (
    <details className="rounded border border-line p-3">
      <summary className="cursor-pointer text-sm font-medium text-slate-100">Advanced actions</summary>
      <div className="mt-3">
        <AIControlStrip patientId={patientId} />
      </div>
    </details>
  );
}

function AIControlStrip({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [packet, setPacket] = useState<any | null>(null);
  const [paperclipTitle, setPaperclipTitle] = useState("RPIS patient intelligence follow-up");
  const [paperclipSummary, setPaperclipSummary] = useState("Internal RPIS review item from Patient Command Center.");
  const [protocolTopic, setProtocolTopic] = useState("Radiation oncology protocol review");
  const review = useMutation({
    mutationFn: () => api.post<AIReviewResponse>(`/patients/${patientId}/ai-review`, {
      apply: false,
      include_stylebook: true,
      include_learning_vault: true,
      mode: "gpt55_primary",
    }),
    onSuccess: (data) => {
      setPacket(data.review_packet);
    },
  });
  const effectivePacket = packet;
  const apply = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/ai-direct-write`, {
      review_packet: effectivePacket,
      mode: "gpt55_primary",
      apply: APPLY_KEYS,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
      qc.invalidateQueries({ queryKey: ["timeline", patientId] });
      qc.invalidateQueries({ queryKey: ["tasks", patientId] });
      qc.invalidateQueries({ queryKey: ["alerts", patientId] });
      qc.invalidateQueries({ queryKey: ["appointments", patientId] });
      qc.invalidateQueries({ queryKey: ["notes", patientId] });
      qc.invalidateQueries({ queryKey: ["ai-entries", patientId] });
    },
  });
  const grok = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/grok-second-opinion`),
  });
  const paperclip = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/paperclip-issue`, {
      title: paperclipTitle,
      issue_type: "clinical_review",
      priority: "medium",
      summary: paperclipSummary,
      dry_run: true,
    }),
  });
  const memory = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/hermes-memory-update`, {
      source: "correction_mode",
      lesson_type: "workflow",
      summary: "Patient Command Center physician correction/style memory update requested.",
      details: { source_ui: "patient_command_center" },
    }),
  });
  const protocol = useMutation({
    mutationFn: () => api.post(`/patients/${patientId}/protocol-research-packet`, {
      topic: protocolTopic,
      clinical_question: "Summarize applicable protocol evidence and guideline checkpoints for this synthetic/internal RPIS packet.",
      disease_site: "other",
      urgency: "routine",
      guideline_focus: "ASTRO/ESTRO/NCCN/other",
    }),
  });
  const canApply = !!effectivePacket && effectivePacket?.model === "gpt-5.5" && review.data?.status !== "primary_model_unavailable";
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={() => review.mutate()} disabled={review.isPending}>
          {review.isPending ? "Running GPT-5.5..." : "Run GPT-5.5 review"}
        </button>
        <button
          className="btn-primary"
          disabled={!canApply || apply.isPending}
          title={canApply ? "Apply saved GPT-5.5 packet to patient record" : "Run GPT-5.5 review first."}
          onClick={() => apply.mutate()}
        >
          {apply.isPending ? "Applying..." : "Apply GPT-5.5 output to record"}
        </button>
        <button className="btn-ghost" disabled={grok.isPending} onClick={() => grok.mutate()}>
          {grok.isPending ? "Asking Grok..." : "Grok second opinion"}
        </button>
        <button className="btn-ghost" disabled={paperclip.isPending} onClick={() => paperclip.mutate()}>
          {paperclip.isPending ? "Checking Paperclip..." : "Send to Paperclip"}
        </button>
        <button className="btn-ghost" disabled={memory.isPending} onClick={() => memory.mutate()}>
          Ask Hermes to update memory
        </button>
        <button className="btn-ghost" disabled={protocol.isPending} onClick={() => protocol.mutate()}>
          Generate protocol research packet
        </button>
        <button className="btn-ghost" onClick={() => qc.invalidateQueries()}>Refresh patient intelligence</button>
        <a className="btn-ghost" href="#correction-mode">Correction mode</a>
      </div>
      <div className="mt-3 grid md:grid-cols-3 gap-2">
        <input className="input" value={paperclipTitle} onChange={(e) => setPaperclipTitle(e.target.value)} placeholder="Paperclip title" />
        <input className="input" value={paperclipSummary} onChange={(e) => setPaperclipSummary(e.target.value)} placeholder="Non-identifying Paperclip summary" />
        <input className="input" value={protocolTopic} onChange={(e) => setProtocolTopic(e.target.value)} placeholder="Protocol topic" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <Badge tone="info">model gpt-5.5</Badge>
        <Badge tone={review.data?.status === "primary_model_unavailable" ? "bad" : review.data ? "ok" : "muted"}>
          {review.data ? review.data.review_status : "not run"}
        </Badge>
        <span>Direct writes create editable, source-linked, auditable records requiring physician review.</span>
      </div>
      {review.error ? <div className="mt-3"><ErrorBox error={review.error} /></div> : null}
      {!canApply && <div className="mt-3 text-xs text-slate-500">Run GPT-5.5 review first.</div>}
      {review.data?.status === "primary_model_unavailable" ? <div className="mt-3 review-banner">GPT-5.5 primary model unavailable. No local clinical fallback was used.</div> : null}
      {apply.data ? <div className="mt-3 review-banner">Applied. Created counts: {JSON.stringify((apply.data as any).counts)}</div> : null}
      {grok.data ? <div className="mt-3 review-banner">Grok second opinion status: {String((grok.data as any).status)}</div> : null}
      {paperclip.data ? <div className="mt-3 review-banner">Paperclip status: {String((paperclip.data as any).result?.status || (paperclip.data as any).status)}</div> : null}
      {memory.data ? <div className="mt-3 review-banner">Hermes memory update path: {String((memory.data as any).memory_update)}</div> : null}
      {protocol.data ? <div className="mt-3 review-banner">Protocol packet created: {String((protocol.data as any).path)}</div> : null}
    </Card>
  );
}

function CommandCenterTab({ id, ov }: { id: string; ov: PatientOverview }) {
  // Default Patient File should behave as a clinical overview. AI review
  // packets remain available from Advanced actions / Correction Mode / RPIS
  // Agent, but empty packet placeholders should not occupy routine review.
  const [latestPacket] = useState<any | null>(null);
  const sourceBackedRows = asRows(latestPacket?.source_backed_facts);
  const inferredRows = asRows(latestPacket?.inferred_facts);
  const contradictionRows = asRows(latestPacket?.contradictions);
  const missingRows = [...(ov.missing_data_flags || []), ...asRows(latestPacket?.missing_or_uncertain_items)];
  const recommendedActions = clinicalOverviewActions(ov);
  const showOperationalPreview = ov.open_tasks_count > 0 || ov.upcoming_appointments_count > 0 || ov.active_alerts_count > 0;

  return (
    <div className="space-y-4">
      {(sourceBackedRows.length || inferredRows.length || contradictionRows.length || missingRows.length) ? (
        <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-4">
          {sourceBackedRows.length ? <ClinicalSummaryCard title="Source-backed facts" rows={sourceBackedRows} empty="" /> : null}
          {inferredRows.length ? <ClinicalSummaryCard title="AI-inferred facts needing review" rows={inferredRows} empty="" tone="warn" /> : null}
          {contradictionRows.length ? <ClinicalSummaryCard title="Contradictions" rows={contradictionRows} empty="" tone="bad" /> : null}
          {missingRows.length ? <ClinicalSummaryCard title="Missing or uncertain items" rows={missingRows} empty="" tone="warn" /> : null}
        </div>
      ) : null}

      {recommendedActions.length ? (
        <Card title="Recommended internal actions">
          <ActionList rows={recommendedActions} />
        </Card>
      ) : null}

      {showOperationalPreview ? <TasksAppointmentsAlertsPanel id={id} ov={ov} /> : null}

      {/* Do not render NextVisitNotePanel here: its GET endpoint creates an
          empty note row. Full note/checklist workflows remain outside the
          clean default overview until a non-creating preview endpoint exists. */}
      <TimelinePanel id={id} previewOnly />
    </div>
  );
}

function clinicalOverviewActions(ov: PatientOverview): string[] {
  const rows: string[] = [];
  const nextAction = String(ov.next_action_text || "").trim();
  if (nextAction && !looksLikeRoutineScheduledFollowUp(nextAction, ov)) rows.push(nextAction);
  if (ov.overdue_tasks_count > 0) rows.push("Resolve overdue tasks");
  if (ov.active_alerts_count > 0) rows.push("Review active alerts and contradictions");
  return rows;
}

function looksLikeRoutineScheduledFollowUp(text: string, ov: PatientOverview): boolean {
  const lower = text.toLowerCase();
  const hasFollowUpLanguage = /follow[- ]?up|appointment|visit|clinic/.test(lower);
  const hasScheduleLanguage = /scheduled|registered|booked|pending|awaiting visit/.test(lower);
  const hasVerifiedAppointment = Boolean(ov.registered_follow_up_date || ov.upcoming_appointments_count > 0);
  return hasFollowUpLanguage && hasScheduleLanguage && hasVerifiedAppointment;
}

function ClinicalSummaryCard({ title, rows, empty, tone = "info" }: { title: string; rows: string[]; empty: string; tone?: Tone }) {
  return (
    <Card title={title}>
      {rows.length === 0 ? <Empty title={empty} /> : <ActionList rows={rows.slice(0, 6)} tone={tone} />}
    </Card>
  );
}

function NextVisitNotePanel({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["next-visit-note", id], queryFn: () => api.get<NextVisitNote>(`/patients/${id}/next-visit-note`) });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState({ title: "", description: "", urgency: "routine" });
  const [newItem, setNewItem] = useState({ title: "", description: "", urgency: "routine" });
  const update = useMutation({
    mutationFn: (body: { content: any; summary: string }) => api.patch<NextVisitNote>(`/patients/${id}/next-visit-note`, body),
    onSuccess: () => {
      setEditingIndex(null);
      qc.invalidateQueries({ queryKey: ["next-visit-note", id] });
      qc.invalidateQueries({ queryKey: ["activity", id] });
    },
  });
  const createTask = useMutation({
    mutationFn: (item: any) => api.post("/tasks", {
      patient_id: id,
      title: item.title,
      description: item.description || "Converted intentionally from Next Visit Suggested Note.",
      priority: item.urgency === "urgent" ? "high" : "normal",
      source: "next_visit_note_manual_convert",
      category: "clinical_action",
    }),
    onSuccess: (_data, item: any) => {
      const content = nextVisitContentWithItemStatus(q.data?.content, item.__index, "converted_to_task");
      update.mutate({ content, summary: nextVisitSummary(content) });
      qc.invalidateQueries({ queryKey: ["tasks", id] });
      qc.invalidateQueries({ queryKey: ["overview", id] });
    },
  });
  if (q.isLoading) return <Card title="Next Visit Suggested Note"><Skeleton /></Card>;
  if (q.error) return <Card title="Next Visit Suggested Note"><ErrorBox error={q.error} /></Card>;
  const content = q.data?.content || { items: [] };
  const items = Array.isArray(content.items) ? content.items : [];
  const activeItems = items
    .map((item: any, index: number) => ({ ...item, __index: index }))
    .filter((item: any) => !["done", "resolved", "dismissed", "converted_to_task"].includes(String(item.status || "open").toLowerCase()));
  const historyItems = items
    .map((item: any, index: number) => ({ ...item, __index: index }))
    .filter((item: any) => ["done", "resolved", "dismissed", "converted_to_task"].includes(String(item.status || "open").toLowerCase()));
  const applyItemChange = (index: number, patch: Record<string, any>) => {
    const next = nextVisitContentWithItemPatch(content, index, patch);
    update.mutate({ content: next, summary: nextVisitSummary(next) });
  };
  const addManualItem = () => {
    const title = newItem.title.trim();
    if (!title) return;
    const next = {
      ...content,
      items: [
        ...items,
        {
          title,
          description: newItem.description.trim(),
          urgency: newItem.urgency || "routine",
          source: "manual_patient_file",
          category: "clinic_prep_note",
          status: "open",
          created_at: new Date().toISOString(),
        },
      ],
    };
    setNewItem({ title: "", description: "", urgency: "routine" });
    update.mutate({ content: next, summary: nextVisitSummary(next) });
  };
  return (
    <Card title="Next Visit Suggested Note" actions={<button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={addManualItem} disabled={!newItem.title.trim() || update.isPending}>Add prep note item</button>}>
      {activeItems.length ? (
        <ul className="space-y-2 text-sm">
          {activeItems.map((it: any) => (
            <li key={it.__index} className="card-tight">
              {editingIndex === it.__index ? (
                <div className="space-y-2">
                  <EditInput label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
                  <textarea className="input min-h-[80px]" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                  <EditInput label="Urgency" value={draft.urgency} onChange={(v) => setDraft({ ...draft, urgency: v })} />
                  <div className="flex flex-wrap gap-2">
                    <button className="btn-primary !px-2.5 !py-1 text-xs" disabled={!draft.title.trim() || update.isPending} onClick={() => applyItemChange(it.__index, { ...draft, status: it.status || "open", edited_at: new Date().toISOString() })}>Save</button>
                    <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => setEditingIndex(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-slate-100">{it.title}</div>
                      {it.description ? <div className="text-slate-400 whitespace-pre-wrap">{it.description}</div> : null}
                    </div>
                    <Badge tone={it.urgency === "urgent" ? "warn" : "muted"}>{it.urgency || "routine"}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => { setDraft({ title: it.title || "", description: it.description || "", urgency: it.urgency || "routine" }); setEditingIndex(it.__index); }}>Edit</button>
                    <button className="btn-ghost !px-2.5 !py-1 text-xs" disabled={update.isPending} onClick={() => applyItemChange(it.__index, { status: "resolved", resolved_at: new Date().toISOString() })}>Mark done</button>
                    <button className="btn-ghost !px-2.5 !py-1 text-xs" disabled={update.isPending} onClick={() => applyItemChange(it.__index, { status: "dismissed", dismissed_at: new Date().toISOString() })}>Dismiss</button>
                    <button className="btn-ghost !px-2.5 !py-1 text-xs" disabled={createTask.isPending} onClick={() => createTask.mutate(it)}>Convert to task</button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : q.data?.summary ? <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans">{q.data.summary}</pre> : <Empty title="No active next-visit checklist items." />}
      {Array.isArray(content.non_action_context) && content.non_action_context.length ? (
        <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-400">
          <div className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Resolved / context, not tasks</div>
          <ul className="list-disc space-y-1 pl-4">{content.non_action_context.map((x: string, i: number) => <li key={`${x}-${i}`}>{x}</li>)}</ul>
        </div>
      ) : null}
      {historyItems.length ? (
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer">Show resolved/dismissed history ({historyItems.length})</summary>
          <ul className="mt-2 space-y-1">{historyItems.map((it: any) => <li key={it.__index}>{it.title} — {it.status}</li>)}</ul>
        </details>
      ) : null}
      <div className="mt-3 grid gap-2 md:grid-cols-[1fr_1fr_120px]">
        <input className="input" placeholder="New prep note item" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} />
        <input className="input" placeholder="Optional detail" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
        <input className="input" placeholder="Urgency" value={newItem.urgency} onChange={(e) => setNewItem({ ...newItem, urgency: e.target.value })} />
      </div>
      {update.error ? <div className="mt-3"><ErrorBox error={update.error} /></div> : null}
      {createTask.error ? <div className="mt-3"><ErrorBox error={createTask.error} /></div> : null}
    </Card>
  );
}

function TimelinePanel({ id, previewOnly = false }: { id: string; previewOnly?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["timeline", id], queryFn: () => api.get<TimelineEvent[]>(`/patients/${id}/timeline?timeline_kind=clinical`) });
  const [form, setForm] = useState({ title: "", event_type: "follow_up", event_date: toLocalDatetimeValue(new Date().toISOString()), description: "" });
  const create = useMutation({
    mutationFn: () => api.post(`/patients/${id}/timeline`, {
      ...blankToNull(form),
      timeline_kind: "clinical",
      event_date: form.event_date ? new Date(form.event_date).toISOString() : new Date().toISOString(),
    }),
    onSuccess: () => {
      setForm({ title: "", event_type: "follow_up", event_date: toLocalDatetimeValue(new Date().toISOString()), description: "" });
      qc.invalidateQueries({ queryKey: ["timeline", id] });
    },
  });
  if (q.isLoading) return previewOnly ? null : <Card title="Clinical chronology"><Skeleton /></Card>;
  if (q.error) return previewOnly ? null : <Card title="Clinical chronology"><ErrorBox error={q.error} /></Card>;
  if (previewOnly && !q.data?.length) return null;
  return (
    <Card title={previewOnly ? "Clinical chronology preview" : "Clinical chronology"}>
      {!previewOnly ? (
        <details className="mb-4 card-tight space-y-2">
          <summary className="cursor-pointer text-sm font-medium text-slate-100">Add clinical event</summary>
          <div className="mt-3 space-y-2">
            <div className="grid md:grid-cols-3 gap-2">
              <EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
              <EditInput label="Type" value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} />
              <EditInput label="Date" type="datetime-local" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} />
            </div>
            <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Clinical event details" />
            <button className="btn-primary" disabled={!form.title.trim() || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Adding..." : "Add clinical event"}</button>
            {create.error ? <ErrorBox error={create.error} /> : null}
          </div>
        </details>
      ) : null}
      {!q.data?.length ? <Empty title="No clinical chronology events yet." /> : (
        <ul className="space-y-3 text-sm">
          {q.data.slice(0, previewOnly ? 3 : 8).map((e) => <TimelineItem key={e.id} patientId={id} event={e} />)}
        </ul>
      )}
    </Card>
  );
}

function TimelineItem({ patientId, event }: { patientId: string; event: TimelineEvent }) {
  const parsed = parseAIText(event.description);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: event.title,
    description: parsed.cleanText || "",
    event_type: event.event_type,
    event_date: toLocalDatetimeValue(event.event_date),
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/${patientId}/timeline/${event.id}`, {
      ...blankToNull(form),
      event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
    }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["timeline", patientId] });
      qc.invalidateQueries({ queryKey: ["activity", patientId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/patients/${patientId}/timeline/${event.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["timeline", patientId] }),
  });
  return (
    <li className="border-l-2 border-line pl-3">
      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
        <span>{formatDateTime(event.event_date)}</span>
        <Badge tone="muted">{event.event_type}</Badge>
        {parsed.metadata ? <AIMetadataBadges metadata={parsed.metadata} /> : null}
      </div>
      <div className="text-slate-100 font-medium mt-1">{event.title}</div>
      {parsed.cleanText && <div className="text-slate-400 mt-1 whitespace-pre-wrap">{parsed.cleanText}</div>}
      <div className="mt-2 flex gap-2">
        <button className="btn-ghost" type="button" onClick={() => setEditing((v) => !v)}>Edit</button>
        <button className="btn-danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>Delete</button>
      </div>
      {editing ? (
        <div className="mt-2 card-tight space-y-2">
          <EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <div className="grid md:grid-cols-2 gap-2">
            <EditInput label="Type" value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} />
            <EditInput label="Date" type="datetime-local" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} />
          </div>
          <textarea className="input min-h-[90px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </div>
      ) : null}
    </li>
  );
}

function TasksAppointmentsAlertsPanel({ id, ov }: { id: string; ov: PatientOverview }) {
  return (
    <div className="grid gap-4">
      {ov.open_tasks_count > 0 ? <Card title="Open tasks"><TasksInline patientId={id} /></Card> : null}
      {ov.upcoming_appointments_count > 0 ? <Card title="Pending appointments / tests"><AppointmentsInline patientId={id} compact activeOnly /></Card> : null}
      {ov.active_alerts_count > 0 ? <Card title="Alerts / contradictions"><AlertsInline patientId={id} /></Card> : null}
    </div>
  );
}

function ClinicalDataTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["clinical-data", id], queryFn: () => api.get<any>(`/patients/${id}/clinical-data`) });
  if (q.isLoading) return <Skeleton />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Empty />;
  const d = q.data;
  return (
    <div className="space-y-4">
      <ClinicalDataEditor patientId={id} data={d} />
      <div className="grid gap-4 xl:grid-cols-2">
        <ClinicalDataSection title="Diagnosis and Biology">
          <div className="grid gap-3 md:grid-cols-2 text-sm">
            <ClinicalDataItem label="Histology" value={d.histology} />
            <ClinicalDataItem label="Grade" value={d.grade} />
            <ClinicalDataItem label="Performance status" value={d.performance_status} />
            <ClinicalDataItem label="ICD-10" value={d.icd10} />
          </div>
          <div className="mt-3 space-y-3">
            <ReceptorDisplay data={d} />
            <MolecularDisplay value={d.molecular_json} />
            <ComorbidityBadges value={d.comorbidities_json} />
            <StructuredNotesSubsections title="Social history" text={d.social_history_text} />
          </div>
        </ClinicalDataSection>

        <ClinicalDataSection title="Stage / Current Disease Status">
          <div className="grid gap-3 md:grid-cols-2 text-sm">
            <ClinicalDataItem label="cTNM" value={d.c_tnm} />
            <ClinicalDataItem label="pTNM" value={d.p_tnm} />
            <ClinicalDataItem label="ypTNM" value={d.yp_tnm} />
            <ClinicalDataItem label="Stage group" value={d.stage_group} />
            <ClinicalDataItem label="M-site codes" value={d.metastatic_site_codes} />
            <ClinicalDataItem label="RT status" value={d.rt_status} />
            <ClinicalDataItem label="HR status" value={d.hr_status} />
            <ClinicalDataItem label="HER2 status" value={d.her2_status} />
          </div>
        </ClinicalDataSection>

        <ClinicalDataSection title="Treatment Summary">
          <div className="grid gap-3 md:grid-cols-2 text-sm">
            <ClinicalDataItem label="Last RT dose / site" value={formatLastRtDoseSite(d)} />
            <ClinicalDataItem label="Last RT end date" value={d.last_rt_end_date} />
            <ClinicalDataItem label="RT dose" value={d.rt_dose} />
            <ClinicalDataItem label="RT fractions" value={d.rt_fractions} />
            <ClinicalDataItem label="RT date" value={d.rt_date} />
          </div>
          <PriorTreatmentLines value={d.prior_treatments_json} />
        </ClinicalDataSection>

        <ClinicalDataSection title="Follow-up Plan">
          <div className="grid gap-3 md:grid-cols-2 text-sm">
            <ClinicalDataItem label="Follow-up interval" value={d.follow_up_interval} />
            <ClinicalDataItem label="Performance status" value={d.performance_status} />
          </div>
          <StructuredNotesSubsections title="Notes" text={d.notes_text} />
        </ClinicalDataSection>

        <ClinicalDataSection title="Data Quality / Source Discrepancy">
          <SourceDiscrepancyHighlights data={d} />
        </ClinicalDataSection>

        <ClinicalDataSection title="Data Completeness">
          <DataCompleteness data={d} />
        </ClinicalDataSection>
      </div>
      <AdvancedRawClinicalData data={d} />
    </div>
  );
}

function ClinicalDataSection({ title, children }: { title: string; children: ReactNode }) {
  return <Card title={title}>{children}</Card>;
}

function ClinicalDataItem({ label, value, multiline = false }: { label: string; value: any; multiline?: boolean }) {
  return (
    <div className="card-tight">
      <div className="label">{label}</div>
      <div className={`text-sm text-slate-100 ${multiline ? "whitespace-pre-wrap" : "break-words"}`}>{clinicalDisplayValue(value)}</div>
    </div>
  );
}

function ReceptorDisplay({ data }: { data: any }) {
  const receptorJson = parseClinicalStructuredValue(data.er_pr_her2_json);
  const rows = receptorEntries(receptorJson, data);
  return (
    <div>
      <div className="label">ER / PR / HER2</div>
      {rows.length ? (
        <div className="flex flex-wrap gap-2">
          {rows.map(([label, value]) => <Badge key={label} tone={receptorTone(value)}>{label}: {clinicalDisplayValue(value)}</Badge>)}
        </div>
      ) : (
        <div className="text-sm text-slate-500">not recorded</div>
      )}
    </div>
  );
}

function MolecularDisplay({ value }: { value: any }) {
  const parsed = parseClinicalStructuredValue(value);
  const rows = clinicalKeyValueLines(parsed);
  return (
    <div>
      <div className="label">Molecular</div>
      {rows.length ? (
        <ul className="space-y-1 text-sm text-slate-200">
          {rows.map((line, i) => <li key={`${line}-${i}`} className="card-tight">{line}</li>)}
        </ul>
      ) : (
        <div className="text-sm text-slate-500">not recorded</div>
      )}
    </div>
  );
}

function ComorbidityBadges({ value }: { value: any }) {
  const rows = clinicalArrayLines(parseClinicalStructuredValue(value));
  return (
    <div>
      <div className="label">Comorbidities</div>
      {rows.length ? (
        <div className="flex flex-wrap gap-2">
          {rows.map((line, i) => <Badge key={`${line}-${i}`} tone="muted">{line}</Badge>)}
        </div>
      ) : (
        <div className="text-sm text-slate-500">not recorded</div>
      )}
    </div>
  );
}

function PriorTreatmentLines({ value }: { value: any }) {
  const rows = clinicalArrayLines(parseClinicalStructuredValue(value));
  return (
    <div className="mt-3">
      <div className="label">Prior treatments</div>
      {rows.length ? (
        <ul className="space-y-2 text-sm">
          {rows.map((line, i) => (
            <li key={`${line}-${i}`} className="flex gap-2 border-b border-line/60 pb-1">
              <Badge tone="info">{i + 1}</Badge>
              <span className="text-slate-200 whitespace-pre-wrap">{line}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-slate-500">not recorded</div>
      )}
    </div>
  );
}

function StructuredNotesSubsections({ title, text }: { title: string; text?: string | null }) {
  const sections = structuredNoteSections(text);
  return (
    <div>
      <div className="label">{title}</div>
      {!sections.length ? (
        <div className="text-sm text-slate-500">not recorded</div>
      ) : (
        <div className="space-y-2">
          {sections.map((section, i) => (
            <div key={`${section.title}-${i}`} className="card-tight">
              {section.title ? <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</div> : null}
              <div className="whitespace-pre-wrap text-sm text-slate-200">{section.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceDiscrepancyHighlights({ data }: { data: any }) {
  const rows = sourceDiscrepancyRows(data);
  if (!rows.length) return <div className="text-sm text-slate-500">No source discrepancies recorded.</div>;
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((row, i) => (
        <li key={`${row}-${i}`} className="rounded border border-amber-800/70 bg-amber-950/20 p-3 text-amber-100">
          <div className="mb-1"><Badge tone="warn">discrepancy</Badge></div>
          <div className="whitespace-pre-wrap">{row}</div>
        </li>
      ))}
    </ul>
  );
}

function DataCompleteness({ data }: { data: any }) {
  const rows = clinicalCompletenessRows(data);
  const notes = dataCompletenessNotes(data);
  const present = rows.filter((row) => row.present).length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
        <Badge tone={present === rows.length ? "ok" : present >= rows.length / 2 ? "warn" : "bad"}>{present}/{rows.length} present</Badge>
        <span>Structured clinical fields used by this view.</span>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="card-tight flex items-center justify-between gap-3">
            <span className="text-sm text-slate-300">{row.label}</span>
            <Badge tone={row.present ? "ok" : "warn"}>{row.present ? "present" : "missing"}</Badge>
          </div>
        ))}
      </div>
      {notes.length ? (
        <div>
          <div className="label">Recorded completeness notes</div>
          <ul className="space-y-1 text-sm text-slate-200">
            {notes.map((note, i) => <li key={`${note}-${i}`} className="card-tight">{note}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function AdvancedRawClinicalData({ data }: { data: any }) {
  return (
    <details className="card p-4">
      <summary className="cursor-pointer text-sm font-semibold text-slate-100">Advanced raw clinical data</summary>
      <pre className="mt-3 max-h-[420px] overflow-auto whitespace-pre-wrap rounded border border-line bg-slate-950/60 p-3 text-xs text-slate-300">{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}

function clinicalDisplayValue(value: any): string {
  if (isClinicalBlank(value)) return "not recorded";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function isClinicalBlank(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "" || v === "null" || v === "undefined";
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

function parseClinicalStructuredValue(value: any): any {
  if (isClinicalBlank(value)) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function receptorEntries(receptorJson: any, data: any): Array<[string, any]> {
  const rows: Array<[string, any]> = [];
  const obj = receptorJson && typeof receptorJson === "object" && !Array.isArray(receptorJson) ? receptorJson : {};
  const add = (label: string, value: any) => {
    if (!isClinicalBlank(value) && !rows.some(([existing]) => existing === label)) rows.push([label, value]);
  };
  add("ER", pickClinicalValue(obj, ["er", "ER", "estrogen_receptor", "estrogen receptor"]) ?? data.er_status);
  add("PR", pickClinicalValue(obj, ["pr", "PR", "progesterone_receptor", "progesterone receptor"]) ?? data.pr_status);
  add("HER2", pickClinicalValue(obj, ["her2", "HER2", "her_2"]) ?? data.her2_status);
  Object.entries(obj).forEach(([key, value]) => {
    const normalized = key.toLowerCase().replace(/[\s_-]+/g, "");
    if (!["er", "estrogenreceptor", "pr", "progesteronereceptor", "her2"].includes(normalized)) add(titleCase(key), value);
  });
  if (!rows.length && typeof receptorJson === "string") rows.push(["ER/PR/HER2", receptorJson]);
  return rows;
}

function receptorTone(value: any): Tone {
  const v = String(value || "").toLowerCase();
  if (/positive|\bpos\b|\+|amplified|overexpress/.test(v)) return "ok";
  if (/negative|\bneg\b|-|not amplified/.test(v)) return "muted";
  if (/equivocal|borderline|pending|unknown|indeterminate/.test(v)) return "warn";
  return "info";
}

function pickClinicalValue(source: Record<string, any>, keys: string[]): any {
  for (const key of keys) {
    if (!isClinicalBlank(source[key])) return source[key];
  }
  const wanted = keys.map((key) => key.toLowerCase().replace(/[\s_-]+/g, ""));
  const match = Object.entries(source).find(([key, value]) =>
    wanted.includes(key.toLowerCase().replace(/[\s_-]+/g, "")) && !isClinicalBlank(value)
  );
  return match?.[1];
}

function clinicalKeyValueLines(value: any): string[] {
  if (isClinicalBlank(value)) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return clinicalArrayLines(value);
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, val]) => !isClinicalBlank(val))
      .map(([key, val]) => `${titleCase(key)}: ${clinicalHumanValue(val)}`);
  }
  return [String(value)];
}

function clinicalArrayLines(value: any): string[] {
  if (isClinicalBlank(value)) return [];
  if (Array.isArray(value)) return value.map(clinicalHumanValue).filter((line) => !isClinicalBlank(line));
  if (typeof value === "object") return clinicalKeyValueLines(value);
  return [String(value)];
}

function clinicalHumanValue(value: any): string {
  if (isClinicalBlank(value)) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(clinicalHumanValue).filter(Boolean).join("; ");
  if (typeof value === "object") {
    const preferred = value.name || value.label || value.title || value.treatment || value.regimen || value.diagnosis || value.condition;
    const details = Object.entries(value)
      .filter(([key, val]) => !["name", "label", "title", "treatment", "regimen", "diagnosis", "condition"].includes(key) && !isClinicalBlank(val))
      .map(([key, val]) => `${titleCase(key)}: ${clinicalHumanValue(val)}`);
    return [preferred ? String(preferred) : "", ...details].filter(Boolean).join(" · ") || JSON.stringify(value);
  }
  return String(value);
}

function structuredNoteSections(text?: string | null): Array<{ title: string; body: string }> {
  if (!text || !text.trim()) return [];
  const expected = [
    "Clinical Summary",
    "Treatment History",
    "Current Disease Status",
    "Source Discrepancy Warning",
    "Plan",
  ];
  const sections = parseExplicitNoteSections(text);
  if (sections.length > 1 || (sections.length === 1 && sections[0].title)) {
    const sectionMap = new Map(sections.map((section) => [canonicalSectionTitle(section.title), section.body]));
    const expectedRows = expected
      .map((title) => ({ title, body: sectionMap.get(canonicalSectionTitle(title)) || "" }))
      .filter((section) => section.body);
    const extraRows = sections
      .filter((section) => !expected.some((title) => canonicalSectionTitle(title) === canonicalSectionTitle(section.title)))
      .map((section) => ({ title: section.title || "Other Notes", body: section.body }));
    return [...expectedRows, ...extraRows].filter((section) => section.body);
  }
  return inferStructuredNoteSections(text);
}

function parseExplicitNoteSections(text: string): Array<{ title: string; body: string }> {
  const lines = text.split(/\r?\n/);
  const sections: Array<{ title: string; body: string[] }> = [];
  let current: { title: string; body: string[] } = { title: "", body: [] };
  const pushCurrent = () => {
    const body = current.body.join("\n").trim();
    if (body) sections.push({ title: current.title, body: current.body });
  };
  lines.forEach((line) => {
    const match = line.match(/^\s*([A-Za-z][A-Za-z0-9 /_-]{1,48}):\s*(.*)$/);
    if (match) {
      pushCurrent();
      current = { title: match[1].trim(), body: match[2] ? [match[2]] : [] };
    } else {
      current.body.push(line);
    }
  });
  pushCurrent();
  return sections.map((section) => ({ title: section.title, body: section.body.join("\n").trim() })).filter((section) => section.body);
}

function inferStructuredNoteSections(text: string): Array<{ title: string; body: string }> {
  const chunks = text.split(/\n{2,}|(?<=\.)\s+(?=[A-Z])/).map((chunk) => chunk.trim()).filter(Boolean);
  const buckets = new Map<string, string[]>();
  const push = (title: string, body: string) => buckets.set(title, [...(buckets.get(title) || []), body]);
  chunks.forEach((chunk) => {
    if (/discrep|contradict|conflict|old metastatic|m0|ned|no evidence of metast/i.test(chunk)) push("Source Discrepancy Warning", chunk);
    else if (/\b(plan|follow|appointment|surveillance|repeat|review|continue|arrange)\b/i.test(chunk)) push("Plan", chunk);
    else if (/\b(chemotherapy|radiotherapy|radiation|surgery|mastectomy|systemic|hormone|endocrine|treated|completed)\b/i.test(chunk)) push("Treatment History", chunk);
    else if (/\b(current|status|imaging|ct|pet|mri|bone scan|ned|progression|recurrence|metastatic|stable)\b/i.test(chunk)) push("Current Disease Status", chunk);
    else push("Clinical Summary", chunk);
  });
  return ["Clinical Summary", "Treatment History", "Current Disease Status", "Source Discrepancy Warning", "Plan"]
    .map((title) => ({ title, body: (buckets.get(title) || []).join("\n\n") }))
    .filter((section) => section.body);
}

function canonicalSectionTitle(title: string): string {
  const normalized = title.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (/treatment|history|prior/.test(normalized)) return "treatmenthistory";
  if (/current|disease|status/.test(normalized)) return "currentdiseasestatus";
  if (/discrep|contradict|conflict|warning/.test(normalized)) return "sourcediscrepancywarning";
  if (/plan|follow/.test(normalized)) return "plan";
  if (/summary|clinical/.test(normalized)) return "clinicalsummary";
  return normalized;
}

function sourceDiscrepancyRows(data: any): string[] {
  const rows: string[] = [];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (!/discrep|contradict|conflict|data_quality|quality_issue/i.test(key)) return;
    clinicalArrayLines(parseClinicalStructuredValue(value)).forEach((line) => rows.push(`${titleCase(key)}: ${line}`));
  });
  const combinedText = [data?.notes_text, data?.social_history_text].filter(Boolean).map(String).join("\n");
  combinedText.split(/\r?\n/).forEach((line) => {
    if (/discrep|contradict|conflict|source mismatch|source differs|old metastatic/i.test(line)) rows.push(line.trim());
  });
  const hasHistoricalMetastatic = /old metastatic|previously metastatic|historical metastatic|metastatic wording|metastatic disease/i.test(combinedText);
  const hasCurrentNonMetastatic = /\bM0\b|cM0|pM0|NED|no evidence of (distant )?(metastatic disease|metastasis)|negative (CT|PET|bone scan|imaging)|current imaging.*negative|physician assessment.*(M0|NED|non-metastatic)/i.test(combinedText);
  if (hasHistoricalMetastatic && hasCurrentNonMetastatic) {
    rows.push("Historical/old metastatic wording appears alongside current M0/NED or negative imaging/physician assessment; review source chronology before treating the patient as metastatic.");
  }
  return Array.from(new Set(rows.filter(Boolean)));
}

function clinicalCompletenessRows(data: any): Array<{ label: string; present: boolean }> {
  const groups = [
    { label: "Histology", keys: ["histology"] },
    { label: "Grade", keys: ["grade"] },
    { label: "Performance status", keys: ["performance_status"] },
    { label: "Stage / TNM", keys: ["c_tnm", "p_tnm", "yp_tnm", "stage_group"] },
    { label: "Receptors", keys: ["er_pr_her2_json", "hr_status", "her2_status"] },
    { label: "Molecular", keys: ["molecular_json"] },
    { label: "Comorbidities", keys: ["comorbidities_json"] },
    { label: "Prior treatments", keys: ["prior_treatments_json"] },
    { label: "Radiotherapy", keys: ["last_rt_dose", "last_rt_site", "last_rt_end_date", "rt_status", "rt_dose", "rt_date"] },
    { label: "Follow-up interval", keys: ["follow_up_interval"] },
    { label: "Social history", keys: ["social_history_text"] },
    { label: "Notes", keys: ["notes_text"] },
  ];
  return groups.map((group) => ({
    label: group.label,
    present: group.keys.some((key) => !isClinicalBlank(data?.[key])),
  }));
}

function dataCompletenessNotes(data: any): string[] {
  const rows: string[] = [];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (!/complete|missing|required|unknown|uncertain/i.test(key)) return;
    if (/discrep|contradict|conflict/i.test(key)) return;
    clinicalArrayLines(parseClinicalStructuredValue(value)).forEach((line) => rows.push(`${titleCase(key)}: ${line}`));
  });
  return Array.from(new Set(rows.filter(Boolean)));
}

function formatLastRtDoseSite(data: any): string {
  const dose = data?.last_rt_dose;
  const site = data?.last_rt_site;
  if (!isClinicalBlank(dose) && !isClinicalBlank(site)) return `${dose} / ${site}`;
  if (!isClinicalBlank(dose)) return String(dose);
  if (!isClinicalBlank(site)) return String(site);
  return "not recorded";
}

function ClinicalDataEditor({ patientId, data }: { patientId: string; data: any }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    histology: data.histology || "",
    grade: data.grade || "",
    performance_status: data.performance_status || "",
    icd10: data.icd10 || "",
    c_tnm: data.c_tnm || "",
    p_tnm: data.p_tnm || "",
    yp_tnm: data.yp_tnm || "",
    metastatic_site_codes: data.metastatic_site_codes || "",
    stage_group: data.stage_group || "",
    follow_up_interval: data.follow_up_interval || "",
    hr_status: data.hr_status || "",
    her2_status: data.her2_status || "",
    rt_status: data.rt_status || "",
    rt_dose: data.rt_dose || "",
    rt_fractions: data.rt_fractions || "",
    rt_date: data.rt_date || "",
    notes_text: data.notes_text || "",
  } as any);
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/${patientId}/clinical-data`, {
      ...blankToNull(form),
      rt_fractions: form.rt_fractions === "" ? null : Number(form.rt_fractions),
    }),
    onSuccess: () => {
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["clinical-data", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
    },
  });
  if (!open) return <button className="btn-primary mb-4" onClick={() => setOpen(true)}>Edit clinical data</button>;
  return (
    <div className="mb-4 card-tight space-y-2">
      <div className="grid md:grid-cols-3 gap-2">
        {Object.keys(form).filter((k) => k !== "notes_text").map((key) => (
          <EditInput key={key} label={key} type={key === "rt_date" ? "date" : key === "rt_fractions" ? "number" : "text"} value={String(form[key] || "")} onChange={(v) => setForm({ ...form, [key]: v })} />
        ))}
      </div>
      <textarea className="input min-h-[100px]" value={form.notes_text} onChange={(e) => setForm({ ...form, notes_text: e.target.value })} />
      <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save clinical data</button><button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button></div>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </div>
  );
}

function ReportsTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["reports", id], queryFn: () => api.get<ReportAttachment[]>(`/patients/${id}/reports`) });
  const review = useMutation({
    mutationFn: (rid: string) => api.post(`/reports/${rid}/mark-reviewed`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", id] });
      qc.invalidateQueries({ queryKey: ["overview", id] });
    },
  });
  return (
    <Card title="Reports & attachments" actions={<UploadReportButton patientId={id} />}>
      {q.isLoading ? <Skeleton /> :
       !q.data?.length ? <Empty title="No reports yet." /> :
       <table className="table">
         <thead><tr><th>Title</th><th>Kind</th><th>Date</th><th>Reviewed</th><th></th></tr></thead>
         <tbody>
           {q.data.map((r) => (
             <tr key={r.id}>
               <td>{r.title}<div className="text-xs text-slate-500">{r.description}</div></td>
               <td><Badge tone="muted">{r.kind}</Badge></td>
               <td>{r.report_date || "-"}</td>
               <td>{r.is_reviewed ? <Badge tone="ok">reviewed</Badge> : <Badge tone="warn">pending</Badge>}</td>
               <td>{!r.is_reviewed && <button className="btn-ghost" onClick={() => review.mutate(r.id)}>Mark reviewed</button>}</td>
             </tr>
           ))}
         </tbody>
       </table>}
    </Card>
  );
}

function UploadReportButton({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("other");
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("kind", kind);
      return api.upload(`/patients/${patientId}/reports/upload`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reports", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
      setOpen(false); setTitle(""); setKind("other");
    },
  });
  return (
    <div className="relative">
      <button className="btn-primary" onClick={() => setOpen((o) => !o)}>Upload</button>
      {open && (
        <div className="absolute right-0 mt-2 z-10 w-80 card p-3 space-y-2">
          <div><label className="label">Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div><label className="label">Kind</label><select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>{["pathology","imaging","lab","clinic_note","external_rt","screenshot","pdf","image","other"].map((k) => <option key={k} value={k}>{k}</option>)}</select></div>
          <input type="file" className="block text-sm" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} />
          {upload.error ? <ErrorBox error={upload.error} /> : null}
        </div>
      )}
    </div>
  );
}

function TimelineTab({ id }: { id: string }) {
  return <TimelinePanel id={id} />;
}

function TasksTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tasks", id], queryFn: () => api.get<Task[]>(`/tasks?patient_id=${id}`) });
  const close = useMutation({
    mutationFn: (tid: string) => api.post(`/tasks/${tid}/close`, { resolution_note: "Closed from patient command center" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks", id] });
      qc.invalidateQueries({ queryKey: ["overview", id] });
    },
  });
  const [title, setTitle] = useState("");
  const create = useMutation({
    mutationFn: () => api.post("/tasks", { patient_id: id, title, priority: "normal" }),
    onSuccess: () => {
      setTitle("");
      qc.invalidateQueries({ queryKey: ["tasks", id] });
      qc.invalidateQueries({ queryKey: ["overview", id] });
    },
  });
  return (
    <Card title="Tasks & reminders" actions={<div className="flex gap-2"><input className="input w-64" placeholder="New task title" value={title} onChange={(e) => setTitle(e.target.value)} /><button className="btn-primary" disabled={!title || create.isPending} onClick={() => create.mutate()}>Add</button></div>}>
      {q.isLoading ? <Skeleton /> : <TaskTable patientId={id} rows={q.data || []} onClose={(tid) => close.mutate(tid)} />}
    </Card>
  );
}

function TaskTable({ patientId, rows, onClose }: { patientId: string; rows: Task[]; onClose: (id: string) => void }) {
  if (!rows.length) return <Empty title="No tasks yet." />;
  return (
    <table className="table">
      <thead><tr><th>Title</th><th>Status</th><th>Priority</th><th>Due</th><th></th></tr></thead>
      <tbody>
        {rows.map((t) => <TaskRow key={t.id} patientId={patientId} task={t} onClose={onClose} />)}
      </tbody>
    </table>
  );
}

function TaskRow({ patientId, task, onClose }: { patientId: string; task: Task; onClose: (id: string) => void }) {
  const parsed = parseAIText(task.description);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: task.title, description: parsed.cleanText || "", status: task.status, priority: task.priority, due_date: task.due_date || "" });
  const save = useMutation({
    mutationFn: () => api.patch(`/tasks/${task.id}`, blankToNull(form)),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["tasks", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
    },
  });
  return (
    <tr>
      <td>
        {editing ? (
          <div className="space-y-2 min-w-[260px]">
            <EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        ) : (
          <>{task.title}<div className="text-xs text-slate-500 whitespace-pre-wrap">{parsed.cleanText}</div>{parsed.metadata ? <AIMetadataBadges metadata={parsed.metadata} /> : null}</>
        )}
      </td>
      <td>{editing ? <input className="input w-36" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} /> : <Badge tone={task.status === "closed" ? "ok" : task.status === "cancelled" ? "muted" : "info"}>{task.status}</Badge>}</td>
      <td>{editing ? <input className="input w-32" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /> : <Badge tone={task.priority === "urgent" ? "bad" : task.priority === "high" ? "warn" : "muted"}>{task.priority}</Badge>}</td>
      <td>{editing ? <input className="input w-36" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /> : task.due_date || "-"}</td>
      <td><div className="flex flex-wrap gap-2">{editing ? <><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></> : <><button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>{!["closed", "cancelled"].includes(task.status) && <button className="btn-ghost" onClick={() => onClose(task.id)}>Close</button>}</>}</div>{save.error ? <ErrorBox error={save.error} /> : null}</td>
    </tr>
  );
}

function TasksInline({ patientId }: { patientId: string }) {
  const q = useQuery({ queryKey: ["tasks", patientId], queryFn: () => api.get<Task[]>(`/tasks?patient_id=${patientId}`) });
  if (q.isLoading) return <Skeleton />;
  return <ActionList rows={(q.data || []).filter((t) => !["closed", "cancelled"].includes(t.status)).slice(0, 6).map((t) => `${t.title}${t.due_date ? ` - due ${t.due_date}` : ""}`)} empty="No open tasks." />;
}

function AppointmentsInline({ patientId, compact = false, activeOnly = false }: { patientId: string; compact?: boolean; activeOnly?: boolean }) {
  const q = useQuery({ queryKey: ["appointments", patientId, activeOnly ? "active" : "all"], queryFn: () => api.get<Appointment[]>(`/appointments?patient_id=${patientId}`) });
  if (q.isLoading) return <Skeleton />;
  const filtered = activeOnly
    ? (q.data || []).filter((a) => ["pending", "scheduled"].includes(String(a.status || "").toLowerCase()))
    : (q.data || []);
  if (!filtered.length) return activeOnly ? null : <Empty title="No appointments." />;
  const rows = compact ? filtered.slice(0, 5) : filtered;
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((a) => <AppointmentRow key={a.id} patientId={patientId} appointment={a} compact={compact} />)}
    </ul>
  );
}

function AppointmentRow({ patientId, appointment, compact }: { patientId: string; appointment: Appointment; compact: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: appointment.title,
    kind: appointment.kind,
    status: appointment.status,
    scheduled_for: toLocalDatetimeValue(appointment.scheduled_for),
    location: appointment.location || "",
    notes: appointment.notes || "",
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/appointments/${appointment.id}`, {
      ...blankToNull(form),
      scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
    }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["appointments", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
    },
  });
  return (
    <li className="border-b border-line/60 py-2">
      {!editing ? (
        <div className="flex justify-between gap-3">
          <div><div className="text-slate-100">{appointment.title}</div><div className="text-xs text-slate-500">{appointment.kind} · {appointment.location || ""}</div></div>
          <div className="text-right"><Badge tone={appointment.status === "scheduled" ? "ok" : appointment.status === "missed" ? "bad" : "warn"}>{appointment.status}</Badge><div className="text-xs text-slate-500">{appointment.scheduled_for ? formatDateTime(appointment.scheduled_for) : "no date"}</div>{!compact ? <button className="btn-ghost mt-1" onClick={() => setEditing(true)}>Edit</button> : null}</div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid md:grid-cols-3 gap-2"><EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} /><EditInput label="Kind" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} /><EditInput label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} /><EditInput label="Scheduled for" type="datetime-local" value={form.scheduled_for} onChange={(v) => setForm({ ...form, scheduled_for: v })} /><EditInput label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} /></div>
          <textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </div>
      )}
    </li>
  );
}

function AppointmentsTab({ id }: { id: string }) {
  return <Card title="Appointments & tests"><AppointmentsInline patientId={id} /></Card>;
}

function AlertsInline({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["alerts", patientId], queryFn: () => api.get<Alert[]>(`/alerts?patient_id=${patientId}&status=open`) });
  const close = useMutation({
    mutationFn: (aid: string) => api.post(`/alerts/${aid}/close`, { reason: "Closed from patient command center" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alerts", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
    },
  });
  if (q.isLoading) return <Skeleton />;
  if (!q.data?.length) return <Empty title="No active alerts." hint="Run watcher if you suspect stale patients." />;
  return (
    <ul className="space-y-2 text-sm">
      {q.data.map((a) => {
        const parsed = parseAIText(a.message);
        return (
          <AlertRow key={a.id} patientId={patientId} alert={a} cleanText={parsed.cleanText} metadata={parsed.metadata} onClose={() => close.mutate(a.id)} />
        );
      })}
    </ul>
  );
}

function AlertRow({ patientId, alert, cleanText, metadata, onClose }: { patientId: string; alert: Alert; cleanText: string; metadata?: AIMetadata; onClose: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: alert.title, message: cleanText || "", severity: alert.severity, status: alert.status });
  const save = useMutation({
    mutationFn: () => api.patch(`/alerts/${alert.id}`, blankToNull(form)),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["alerts", patientId] });
      qc.invalidateQueries({ queryKey: ["overview", patientId] });
    },
  });
  return (
    <li className="border border-line rounded-lg p-2">
      {!editing ? (
        <>
          <div className="flex items-center justify-between gap-2"><div className="font-medium text-slate-100">{alert.title}</div><Badge tone={alert.severity === "critical" ? "bad" : alert.severity === "warn" ? "warn" : "info"}>{alert.severity}</Badge></div>
          <div className="text-xs text-slate-400">{alert.kind}</div>
          {cleanText && <div className="text-slate-300 mt-1 whitespace-pre-wrap">{cleanText}</div>}
          {metadata ? <div className="mt-2"><AIMetadataBadges metadata={metadata} /></div> : null}
          <div className="mt-2 flex gap-2"><button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button><button className="btn-ghost" onClick={onClose}>Close alert</button></div>
        </>
      ) : (
        <div className="space-y-2">
          <EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <div className="grid md:grid-cols-2 gap-2"><EditInput label="Severity" value={form.severity} onChange={(v) => setForm({ ...form, severity: v })} /><EditInput label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} /></div>
          <textarea className="input min-h-[80px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </div>
      )}
    </li>
  );
}


function MediaLabsTab({ id }: { id: string }) {
  return (
    <div className="grid xl:grid-cols-2 gap-4">
      <MediaTab id={id} />
      <LabsTab id={id} />
    </div>
  );
}

function MediaTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["media", id], queryFn: () => api.get<PatientMedia[]>(`/patients/${id}/media`) });
  const [bodySite, setBodySite] = useState("");
  const [context, setContext] = useState("");
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("media_type", "photo");
      fd.append("body_site", bodySite);
      fd.append("clinical_context", context);
      fd.append("review_status", "needs_review");
      return api.upload(`/patients/${id}/media`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media", id] }),
  });
  return (
    <Card title="Clinical media / photos">
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-2">
          <div><label className="label">Body site</label><input className="input" value={bodySite} onChange={(e) => setBodySite(e.target.value)} placeholder="skin, breast, arm, wound" /></div>
          <div><label className="label">Clinical context</label><input className="input" value={context} onChange={(e) => setContext(e.target.value)} placeholder="dermatitis, fibrosis, edema" /></div>
          <input type="file" accept="image/png,image/jpeg,image/webp" className="block text-sm" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} />
          <div className="text-xs text-slate-500">Stored only in RPIS storage. No external upload or sharing.</div>
          {upload.error ? <ErrorBox error={upload.error} /> : null}
        </div>
        <div>{q.isLoading ? <Skeleton /> : <MediaGrid patientId={id} rows={q.data || []} />}</div>
      </div>
    </Card>
  );
}

function MediaGrid({ patientId, rows }: { patientId: string; rows: PatientMedia[] }) {
  if (!rows.length) return <Empty title="No clinical photos yet." hint="Comparison over time is staged for viewer V2." />;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
      {rows.map((m) => <MediaCard key={m.id} patientId={patientId} media={m} />)}
    </div>
  );
}

function MediaCard({ patientId, media }: { patientId: string; media: PatientMedia }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    body_site: media.body_site || "",
    clinical_context: media.clinical_context || "",
    taken_at: toLocalDatetimeValue(media.taken_at),
    description: media.description || "",
    review_status: media.review_status,
  });
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/media/${media.id}`, {
      ...blankToNull(form),
      taken_at: form.taken_at ? new Date(form.taken_at).toISOString() : null,
    }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["media", patientId] });
    },
  });
  return (
    <div className="card-tight">
      <AuthenticatedMediaImage media={media} />
      {!editing ? (
        <>
          <div className="mt-2 flex gap-2 flex-wrap"><Badge tone="info">{media.media_type}</Badge><Badge tone={media.review_status === "reviewed" ? "ok" : "warn"}>{media.review_status}</Badge></div>
          <div className="mt-2 text-sm text-slate-100">{media.body_site || "Unspecified site"}</div>
          <div className="text-xs text-slate-500">{media.clinical_context || "-"} · {media.taken_at ? formatDateTime(media.taken_at) : formatDateTime(media.created_at)}</div>
          {media.description ? <div className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{media.description}</div> : null}
          <div className="mt-2 flex gap-2"><button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button><span className="text-xs text-slate-500">Click preview to open image.</span></div>
        </>
      ) : (
        <div className="mt-2 space-y-2">
          <EditInput label="Body site" value={form.body_site} onChange={(v) => setForm({ ...form, body_site: v })} />
          <EditInput label="Clinical context" value={form.clinical_context} onChange={(v) => setForm({ ...form, clinical_context: v })} />
          <EditInput label="Taken at" type="datetime-local" value={form.taken_at} onChange={(v) => setForm({ ...form, taken_at: v })} />
          <EditInput label="Review status" value={form.review_status} onChange={(v) => setForm({ ...form, review_status: v })} />
          <textarea className="input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </div>
      )}
    </div>
  );
}

function AuthenticatedMediaImage({ media }: { media: PatientMedia }) {
  const [src, setSrc] = useState<string | null>(null);
  const path = `/api/v1/patients/media/${media.id}/file`;
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    const token = localStorage.getItem("rpis_token");
    fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((resp) => {
        if (!resp.ok) throw new Error("media preview unavailable");
        return resp.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  return (
    <a className="block aspect-video rounded bg-slate-900 border border-line overflow-hidden" href={src || path} target="_blank" rel="noreferrer">
      {src ? <img src={src} alt={media.body_site || "Clinical media"} className="h-full w-full object-cover" /> : <div className="h-full flex items-center justify-center text-xs text-slate-500">Preview unavailable</div>}
    </a>
  );
}

function LabsTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["labs", id], queryFn: () => api.get<PatientLab[]>(`/patients/${id}/labs`) });
  const [form, setForm] = useState({ lab_name: "", value: "", unit: "", date: "", source: "manual", abnormal: false });
  const create = useMutation({
    mutationFn: () => api.post(`/patients/${id}/labs`, { ...form, date: form.date || null }),
    onSuccess: () => {
      setForm({ lab_name: "", value: "", unit: "", date: "", source: "manual", abnormal: false });
      qc.invalidateQueries({ queryKey: ["labs", id] });
    },
  });
  return (
    <Card title="Labs">
      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        <div className="space-y-2">
          <input className="input" placeholder="Lab name, e.g. PSA" value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2"><input className="input" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /><input className="input" placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
          <input className="input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.abnormal} onChange={(e) => setForm({ ...form, abnormal: e.target.checked })} /> Abnormal</label>
          <button className="btn-primary" disabled={!form.lab_name || !form.value || create.isPending} onClick={() => create.mutate()}>Add lab</button>
          <div className="text-xs text-slate-500">Trend chart viewer is staged; structured lab rows are active.</div>
        </div>
        <div>{q.isLoading ? <Skeleton /> : <LabsTable patientId={id} rows={q.data || []} />}</div>
      </div>
    </Card>
  );
}

function LabsTable({ patientId, rows }: { patientId: string; rows: PatientLab[] }) {
  if (!rows.length) return <Empty title="No labs recorded." />;
  return (
    <table className="table">
      <thead><tr><th>Date</th><th>Lab</th><th>Value</th><th>Source</th><th>Flag</th><th></th></tr></thead>
      <tbody>{rows.map((l) => <LabRow key={l.id} patientId={patientId} lab={l} />)}</tbody>
    </table>
  );
}

function LabRow({ patientId, lab }: { patientId: string; lab: PatientLab }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ lab_name: lab.lab_name, value: lab.value, unit: lab.unit || "", date: lab.date || "", source: lab.source, abnormal: lab.abnormal, notes: lab.notes || "" });
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/labs/${lab.id}`, blankToNull(form)),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["labs", patientId] });
    },
  });
  return (
    <tr>
      <td>{editing ? <input className="input w-36" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /> : lab.date || "-"}</td>
      <td>{editing ? <input className="input w-36" value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} /> : lab.lab_name}</td>
      <td>{editing ? <div className="flex gap-2"><input className="input w-28" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /><input className="input w-24" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div> : `${lab.value} ${lab.unit || ""}`}</td>
      <td>{editing ? <input className="input w-32" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /> : lab.source}</td>
      <td>{editing ? <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.abnormal} onChange={(e) => setForm({ ...form, abnormal: e.target.checked })} /> abnormal</label> : lab.abnormal ? <Badge tone="warn">abnormal</Badge> : <Badge tone="muted">normal</Badge>}</td>
      <td><div className="flex gap-2">{editing ? <><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></> : <button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>}</div>{save.error ? <ErrorBox error={save.error} /> : null}</td>
    </tr>
  );
}


function DiseaseTrackersTab({ id, patient }: { id: string; patient: Patient }) {
  const labs = useQuery({ queryKey: ["labs", id], queryFn: () => api.get<PatientLab[]>(`/patients/${id}/labs`) });
  const clinical = useQuery({ queryKey: ["clinical-data", id], queryFn: () => api.get<any>(`/patients/${id}/clinical-data`) });

  const rawPatient: any = (patient || {}) as any;
  const rawClinical: any = clinical.data || {};
  const allLabs = labs.data || [];

  const diseaseText = [
    rawPatient.primary_diagnosis,
    rawPatient.diagnosis_group,
    rawPatient.stage_summary,
    rawPatient.current_status,
    rawPatient.status_label,
    rawClinical.primary_diagnosis,
    rawClinical.diagnosis_group,
    rawClinical.biomarker_summary,
  ].filter(Boolean).join(" ").toLowerCase();

  const hasPsaOrTestosterone = allLabs.some((l) =>
    /psa|testosterone/i.test(`${l.lab_name || ""} ${l.source || ""}`)
  );

  const isBreast = /breast|c50|her2|hr\+|er\+|pr\+|mastectomy|axilla|tamoxifen|letrozole/i.test(diseaseText);
  const isProstate = !isBreast && (/prostate|psa|gleason|isup|adenocarcinoma of prostate/i.test(diseaseText) || hasPsaOrTestosterone);

  const treatmentPhase = rawPatient.treatment_phase || rawPatient.phase || "not recorded";
  const rtStatus =
    rawClinical.rt_status ||
    rawClinical.radiotherapy_status ||
    rawPatient.rt_status ||
    rawPatient.radiotherapy_status ||
    rawPatient.rt_summary ||
    "not recorded";

  const psaLabs = allLabs.filter((l) => /psa/i.test(l.lab_name || ""));
  const latestPsa = psaLabs[0];
  const testosteroneLabs = allLabs.filter((l) => /testosterone/i.test(l.lab_name || ""));
  const latestTestosterone = testosteroneLabs[0];

  if (labs.isLoading || clinical.isLoading) return <Skeleton />;

  return (
    <div className="grid xl:grid-cols-2 gap-4">
      {isBreast ? (
        <Card title="Breast tracker">
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <StatusLine label="Disease site" value="Breast" />
            <StatusLine label="Treatment phase" value={treatmentPhase} />
            <StatusLine label="HR status" value={rawClinical.hormone_receptor_status || rawClinical.hr_status || rawClinical.er_pr_status || "not recorded"} />
            <StatusLine label="HER2 status" value={rawClinical.her2_status || "not recorded"} />
            <StatusLine label="cTNM" value={rawPatient.c_tnm || rawClinical.c_tnm || "not recorded"} />
            <StatusLine label="pTNM" value={rawPatient.p_tnm || rawClinical.p_tnm || "not recorded"} />
            <StatusLine label="ypTNM" value={rawPatient.yp_tnm || rawClinical.yp_tnm || "not recorded"} />
            <StatusLine label="M-sites" value={rawPatient.metastatic_site_codes || rawClinical.metastatic_site_codes || "not recorded"} />
            <StatusLine label="Stage group" value={rawPatient.stage_group || rawClinical.stage_group || rawPatient.stage_summary || "not recorded"} />
            <StatusLine label="RT history" value={radiotherapySummary(patient, rawClinical)} />
            <StatusLine label="Bone health plan" value={rawClinical.bone_health_plan || "not recorded"} multiline />
            <StatusLine label="CTCAE / toxicity" value={rawClinical.ctcae_summary || rawClinical.toxicity_summary || "not recorded"} multiline />
            <StatusLine label="Arm edema / lymphedema" value={rawClinical.arm_edema || rawClinical.lymphedema || "not recorded"} />
            <StatusLine label="Recurrence signs" value={rawClinical.recurrence_status || "not recorded"} />
            <StatusLine label="Follow-up interval" value={rawPatient.follow_up_interval || rawClinical.follow_up_interval || "not recorded"} />
          </div>
        </Card>
      ) : null}

      {isProstate ? (
        <Card title="Prostate tracker">
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <StatusLine label="Disease site" value="Prostate" />
            <StatusLine label="Treatment phase" value={treatmentPhase} />
            <StatusLine label="PSA timeline" value={latestPsa ? `${latestPsa.value} ${latestPsa.unit || ""} · ${latestPsa.date || ""}` : "not recorded"} />
            <StatusLine label="Testosterone" value={latestTestosterone ? `${latestTestosterone.value} ${latestTestosterone.unit || ""} · ${latestTestosterone.date || ""}` : "not recorded"} />
            <StatusLine label="ADT status" value={rawClinical.adt_status || "not recorded"} />
            <StatusLine label="RT status" value={rtStatus} />
          </div>
        </Card>
      ) : null}

      {!isBreast && !isProstate ? (
        <Card title="Disease tracker">
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <StatusLine label="Diagnosis group" value={rawPatient.diagnosis_group || "not recorded"} />
            <StatusLine label="Treatment phase" value={treatmentPhase} />
            <StatusLine label="Current status" value={rawPatient.status_label || rawPatient.current_status || "not recorded"} />
            <StatusLine label="RT history" value={radiotherapySummary(patient, rawClinical)} />
          </div>
        </Card>
      ) : null}

      <Card title="Structured labs">
        {!allLabs.length ? (
          <Empty title="No labs recorded." />
        ) : (
          <LabsTable patientId={id} rows={allLabs} />
        )}
      </Card>
    </div>
  );
}

function CorrectionModeTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const notes = useQuery({ queryKey: ["notes", id], queryFn: () => api.get<Note[]>(`/patients/${id}/notes`) });
  const timeline = useQuery({ queryKey: ["timeline", id], queryFn: () => api.get<TimelineEvent[]>(`/patients/${id}/timeline`) });
  const aiEntries = useQuery({ queryKey: ["ai-entries", id], queryFn: () => api.get<AIEntry[]>(`/patients/${id}/ai-entries`) });
  const tasks = useQuery({ queryKey: ["tasks", id], queryFn: () => api.get<Task[]>(`/tasks?patient_id=${id}`) });
  const alerts = useQuery({ queryKey: ["alerts", id], queryFn: () => api.get<Alert[]>(`/alerts?patient_id=${id}&status=open`) });
  const [reason, setReason] = useState("Physician reviewed from correction mode");
  const correction = useMutation({
    mutationFn: (body: any) => api.post(`/patients/${id}/corrections`, { reason, ...body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", id] });
      qc.invalidateQueries({ queryKey: ["timeline", id] });
      qc.invalidateQueries({ queryKey: ["ai-entries", id] });
      qc.invalidateQueries({ queryKey: ["tasks", id] });
      qc.invalidateQueries({ queryKey: ["alerts", id] });
      qc.invalidateQueries({ queryKey: ["overview", id] });
    },
  });
  const aiNotes = (notes.data || []).filter((n) => parseAIText(n.body_text).metadata);
  const aiTimeline = (timeline.data || []).filter((e) => parseAIText(e.description).metadata);
  const normalizedNotes = (aiEntries.data || []).filter((e) => e.entity_type === "note");
  const normalizedTimeline = (aiEntries.data || []).filter((e) => e.entity_type === "timeline");
  return (
    <div id="correction-mode" className="space-y-4">
      <Card title="Correction reason">
        <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-2">
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="btn-ghost" onClick={() => correction.mutate({ target_type: "patient", action: "add_next_action", value: reason })}>Add next action</button>
          <button className="btn-ghost" onClick={() => correction.mutate({ target_type: "patient", action: "add_contradiction_task" })}>Add contradiction task</button>
          <button className="btn-ghost" onClick={() => correction.mutate({ target_type: "patient", action: "mark_internal_test" })}>Mark internal/test</button>
        </div>
        {correction.error ? <div className="mt-3"><ErrorBox error={correction.error} /></div> : null}
      </Card>
      <div className="grid xl:grid-cols-2 gap-4">
        <CorrectionList title="AI-authored notes" rows={(normalizedNotes.length ? normalizedNotes : aiNotes.map((n) => ({ entity_id: n.id, title: n.title, clean_text: parseAIText(n.body_text).cleanText }))).map((n: any) => ({ id: n.entity_id, title: n.title, target_type: "note", text: n.clean_text }))} correction={correction.mutate} />
        <CorrectionList title="AI-authored timeline entries" rows={(normalizedTimeline.length ? normalizedTimeline : aiTimeline.map((e) => ({ entity_id: e.id, title: e.title, clean_text: parseAIText(e.description).cleanText }))).map((e: any) => ({ id: e.entity_id, title: e.title, target_type: "timeline", text: e.clean_text }))} correction={correction.mutate} />
        <CorrectionList title="Open tasks" rows={(tasks.data || []).filter((t) => !["closed", "cancelled"].includes(t.status)).map((t) => ({ id: t.id, title: t.title, target_type: "task", text: t.description || "" }))} correction={correction.mutate} closeOnly />
        <CorrectionList title="Open alerts" rows={(alerts.data || []).map((a) => ({ id: a.id, title: a.title, target_type: "alert", text: a.message || "" }))} correction={correction.mutate} closeOnly />
      </div>
    </div>
  );
}

function CorrectionList({ title, rows, correction, closeOnly = false }: { title: string; rows: any[]; correction: (body: any) => void; closeOnly?: boolean }) {
  return (
    <Card title={title}>
      {!rows.length ? <Empty title="Nothing to review." /> : (
        <ul className="space-y-3 text-sm">
          {rows.map((r) => (
            <li key={r.id} className="card-tight">
              <div className="font-medium text-slate-100">{r.title}</div>
              {r.text ? <div className="mt-1 text-slate-400 whitespace-pre-wrap">{r.text}</div> : null}
              <div className="mt-2 flex flex-wrap gap-2">
                {!closeOnly && <button className="btn-ghost" onClick={() => correction({ target_type: r.target_type, target_id: r.id, action: "mark_reviewed" })}>Mark reviewed</button>}
                {!closeOnly && <button className="btn-ghost" onClick={() => correction({ target_type: r.target_type, target_id: r.id, action: "mark_edited" })}>Mark edited</button>}
                <button className="btn-danger" onClick={() => correction({ target_type: r.target_type, target_id: r.id, action: closeOnly ? "close" : "reject" })}>{closeOnly ? "Close with reason" : "Reject/archive"}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CaseManagerTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["cm", id], queryFn: () => api.get<CaseManagerMessage[]>(`/patients/${id}/case-manager-messages`) });
  const [template, setTemplate] = useState("coordinate_ct_simulation");
  const create = useMutation({
    mutationFn: () => api.post(`/patients/${id}/case-manager-messages`, { template_key: template, priority: "normal", body_text: "" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cm", id] }),
  });
  return (
    <Card title="Case manager messages" actions={<div className="flex gap-2"><select className="input w-60" value={template} onChange={(e) => setTemplate(e.target.value)}>{["schedule_appointment","arrange_imaging","request_pathology_report","request_previous_rt_records","coordinate_ct_simulation","arrange_dental_clearance","follow_up_missed_appointment","remind_patient_labs_tests","confirm_task_completion","custom"].map((t) => <option key={t} value={t}>{t}</option>)}</select><button className="btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>Generate</button></div>}>
      {q.isLoading ? <Skeleton /> : !q.data?.length ? <Empty title="No messages yet." /> : <div className="space-y-3">{q.data.map((m) => <div key={m.id} className="card-tight"><div className="flex justify-between items-center"><div className="text-sm text-slate-200 font-medium">{m.template_key}</div><Badge tone="info">{m.status}</Badge></div><pre className="whitespace-pre-wrap text-sm text-slate-200 mt-2 font-sans">{m.body_text}</pre></div>)}</div>}
    </Card>
  );
}


function NotesTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notes", id], queryFn: () => api.get<Note[]>(`/patients/${id}/notes`) });
  const [kind, setKind] = useState("follow_up");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const create = useMutation({
    mutationFn: () => api.post(`/patients/${id}/notes`, { kind, title: title || `Note (${kind})`, body_text: body, is_draft: true }),
    onSuccess: () => { setTitle(""); setBody(""); qc.invalidateQueries({ queryKey: ["notes", id] }); },
  });
  return (
    <Card title="Clinical notes">
      <div className="grid md:grid-cols-2 gap-4">
        <div><ReviewBanner /><div className="mt-3 space-y-2"><select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>{["consultation","follow_up","otv","palliative_rt","mdt_summary","completion","patient_friendly"].map((k) => <option key={k} value={k}>{k}</option>)}</select><input className="input" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} /><textarea className="input min-h-[140px]" value={body} onChange={(e) => setBody(e.target.value)} /><button className="btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>Save draft note</button></div></div>
        <div>{q.isLoading ? <Skeleton /> : !q.data?.length ? <Empty title="No notes yet." /> : <ul className="space-y-3">{q.data.map((n) => <NoteRow key={n.id} patientId={id} note={n} />)}</ul>}</div>
      </div>
    </Card>
  );
}

function NoteRow({ patientId, note }: { patientId: string; note: Note }) {
  const parsed = parseAIText(note.body_text);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: note.title, kind: note.kind, body_text: parsed.cleanText || "", is_draft: note.is_draft });
  const save = useMutation({
    mutationFn: () => api.patch(`/patients/${patientId}/notes/${note.id}`, form),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["notes", patientId] });
      qc.invalidateQueries({ queryKey: ["ai-entries", patientId] });
    },
  });
  return (
    <li className="card-tight">
      {!editing ? (
        <>
          <div className="flex justify-between gap-2"><div className="font-medium text-slate-100">{note.title}</div><Badge tone="info">{note.kind}</Badge></div>
          {parsed.metadata ? <div className="mt-2"><AIMetadataBadges metadata={parsed.metadata} /></div> : null}
          <pre className="whitespace-pre-wrap text-sm text-slate-200 mt-2 font-sans">{parsed.cleanText}</pre>
          <button className="btn-ghost mt-2" onClick={() => setEditing(true)}>Edit</button>
        </>
      ) : (
        <div className="space-y-2">
          <EditInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <EditInput label="Kind" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} />
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_draft} onChange={(e) => setForm({ ...form, is_draft: e.target.checked })} /> Draft</label>
          {parsed.metadata ? <AIMetadataBadges metadata={parsed.metadata} /> : null}
          <textarea className="input min-h-[160px]" value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} />
          <div className="flex gap-2"><button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button></div>
          {save.error ? <ErrorBox error={save.error} /> : null}
        </div>
      )}
    </li>
  );
}

function AgentTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["ai-entries", id], queryFn: () => api.get<AIEntry[]>(`/patients/${id}/ai-entries`) });
  return (
    <div className="grid xl:grid-cols-2 gap-4">
      <Card title="RPIS Agent - Patient File AI entries">
        {q.isLoading ? <Skeleton /> : !q.data?.length ? <Empty title="No AI-authored Patient File entries yet." hint="Run review and apply directly to the Patient File when clinically safe." /> : <ul className="space-y-2 text-sm">{q.data.slice(0, 8).map((entry) => <li key={`${entry.entity_type}-${entry.entity_id}`} className="card-tight"><div className="flex items-center justify-between"><div className="text-slate-200">{entry.title}</div><Badge tone={entry.review_status === "ai_written" ? "ok" : "muted"}>{entry.entity_type}</Badge></div><div className="mt-1 text-xs text-slate-400 whitespace-pre-wrap">{entry.clean_text.slice(0, 280)}</div></li>)}</ul>}
      </Card>
      <Card title="Integration status">
        <div className="space-y-2 text-sm">
          <StatusLine label="Patient File AI entries" value={q.data?.length ? `${q.data.length} direct Patient File entries` : "No direct entries found"} tone={q.data?.length ? "ok" : "muted"} />
          <StatusLine label="Grok second opinion" value="Returns patient-bound second opinion packet; no separate queue row is created" tone="info" />
          <StatusLine label="Hermes memory/style" value="Active through Learning Vault memory update endpoint" tone="info" />
          <StatusLine label="Paperclip linked work" value="Active dry-run/live internal issue endpoint" tone="info" />
          <StatusLine label="Learning Vault" value="Direct write, review, correction, workflow events append" tone="info" />
        </div>
      </Card>
    </div>
  );
}

function ActivityTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["activity", id], queryFn: () => api.get<ActivityRow[]>(`/patients/${id}/activity`) });
  return (
    <Card title="Activity log">
      {q.isLoading ? <Skeleton /> : !q.data?.length ? <Empty title="No activity recorded yet." /> : <table className="table"><thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Entity</th></tr></thead><tbody>{q.data.map((a) => <tr key={a.id}><td className="text-xs text-slate-500">{formatDateTime(a.at)}</td><td className="text-slate-300">{a.actor_role || "-"}</td><td className="text-slate-100">{a.action}</td><td className="text-slate-400">{a.entity_kind || ""}</td></tr>)}</tbody></table>}
    </Card>
  );
}

function Tile({ label, value, tone = "muted" }: { label: string; value: number; tone?: Tone }) {
  return <div className="card-tight flex items-center justify-between gap-3"><span className="text-slate-400">{label}</span><Badge tone={tone}>{value}</Badge></div>;
}

function MetricLink({ label, value }: { label: string; value: any }) {
  return (
    <a href={metricAnchor(label)} className="card-tight flex items-center justify-between gap-3 hover:border-cyan-500 transition">
      <span className="text-slate-400">{label}</span>
      <Badge tone={Number(value || 0) > 0 ? "info" : "muted"}>{value ?? 0}</Badge>
    </a>
  );
}

function StatusLine({ label, value, tone = "muted", multiline = false }: { label: string; value: any; tone?: Tone; multiline?: boolean }) {
  return <div className="card-tight"><div className="label">{label}</div><div className="flex items-start justify-between gap-2"><span className={`text-slate-100 ${multiline ? "whitespace-pre-wrap" : ""}`}>{value || "-"}</span><Badge tone={tone}>{tone}</Badge></div></div>;
}

function Field({ label, value, multiline = false }: { label: string; value: any; multiline?: boolean }) {
  const v = value === null || value === undefined || value === "" || value === "null" ? "-" : value;
  return <div><div className="label">{label}</div><div className={`text-slate-100 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{String(v)}</div></div>;
}

function EditInput({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return <div><label className="label">{label}</label><input className="input" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}

function blankToNull<T extends Record<string, any>>(value: T): T {
  return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, val === "" ? null : val])) as T;
}

function metricAnchor(label: string): string {
  const v = label.toLowerCase();
  if (v.includes("task") || v.includes("overdue")) return "#tasks";
  if (v.includes("upcoming")) return "#appointments";
  if (v.includes("alert")) return "#alerts";
  if (v.includes("report")) return "#reports";
  return "#overview";
}

function ActionList({ rows, empty = "Nothing here.", tone = "info" }: { rows: string[]; empty?: string; tone?: Tone }) {
  if (!rows.length) return <Empty title={empty} />;
  return <ul className="space-y-2 text-sm">{rows.map((r, i) => <li key={`${r}-${i}`} className="flex gap-2 border-b border-line/60 pb-1"><Badge tone={tone}>{i + 1}</Badge><span className="text-slate-200 whitespace-pre-wrap">{r}</span></li>)}</ul>;
}

function AIMetadataBadges({ metadata }: { metadata: AIMetadata }) {
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      <Badge tone="info">AI-authored</Badge>
      <Badge tone="muted">{metadata.model || "gpt-5.5"}</Badge>
      {metadata.review_status ? <Badge tone={metadata.review_status === "ai_written" ? "ok" : "warn"}>{metadata.review_status}</Badge> : null}
      {metadata.confidence !== undefined && metadata.confidence !== null ? <Badge tone="muted">confidence {String(metadata.confidence)}</Badge> : null}
      {metadata.source_hash ? <Badge tone="muted">hash {metadata.source_hash.slice(0, 10)}</Badge> : null}
      {metadata.source_ref ? <Badge tone="muted">source {String(metadata.source_ref).slice(0, 24)}</Badge> : null}
    </span>
  );
}

function parseAIText(text?: string | null): { cleanText: string; metadata?: AIMetadata } {
  if (!text) return { cleanText: "" };
  const marker = "AI_METADATA_JSON:";
  const idx = text.indexOf(marker);
  if (idx < 0) return { cleanText: text };
  const cleanText = text.slice(0, idx).trim();
  const raw = text.slice(idx + marker.length).trim();
  const firstLine = raw.split("\n\nPHYSICIAN_CORRECTION_JSON:")[0].trim();
  try {
    return { cleanText, metadata: JSON.parse(firstLine) };
  } catch {
    return { cleanText };
  }
}

function asRows(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return String(item);
    return String(item.title || item.label || item.statement || item.message || item.value || item.description || JSON.stringify(item));
  }).filter(Boolean);
}

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalDatetimeValue(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

function valueFromText(text: string | null | undefined, needle: string): string | null {
  if (!text) return null;
  const line = text.split(/\n|\.|;/).find((part) => part.toLowerCase().includes(needle));
  return line?.trim() || null;
}

function normalizedSex(value?: string | null): string {
  const v = String(value || "").trim().toLowerCase();
  if (["f", "female"].includes(v)) return "female";
  if (["m", "male"].includes(v)) return "male";
  return "sex not recorded";
}

function normalizedSexShort(value?: string | null): string {
  const v = String(value || "").trim().toLowerCase();
  if (["f", "female"].includes(v)) return "F";
  if (["m", "male"].includes(v)) return "M";
  return "sex ?";
}

function titleCase(value?: string | null): string {
  const v = String(value || "").replace(/[_-]+/g, " ").trim();
  return v ? v.replace(/\b\w/g, (m) => m.toUpperCase()) : "-";
}

function normalizeTone(value?: string | null): Tone {
  return ["ok", "warn", "bad", "info", "muted"].includes(String(value || ""))
    ? (value as Tone)
    : "muted";
}

function ageLabel(dob?: string | null): string {
  if (!dob) return "age not recorded";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "age not recorded";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const hadBirthday = now.getMonth() > d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
  if (!hadBirthday) age -= 1;
  return `${age}y`;
}

function ClinicalHeaderRow({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-slate-100">{value || "-"}</span>
    </div>
  );
}

type HeaderSummary = {
  diagnosis: string;
  icd10: string;
  stage: string;
  postOp: string;
  group: string;
  plan: string;
};

function patientHeaderSummary(patient: Patient, clinical?: any): HeaderSummary {
  const raw: any = (patient || {}) as any;
  const c = clinical || {};
  const text = [
    raw.primary_diagnosis,
    raw.diagnosis_group,
    raw.stage_summary,
    raw.c_tnm,
    raw.p_tnm,
    raw.stage_group,
    raw.metastatic_site_codes,
    c.primary_diagnosis,
    c.diagnosis,
    c.c_tnm,
    c.p_tnm,
    c.stage_group,
    c.metastatic_site_codes,
    c.notes_text,
  ].filter(Boolean).join(" ");
  const group = diagnosisGroupLabel(raw.diagnosis_group || c.diagnosis_group || raw.primary_diagnosis || text);
  const diagnosis = cleanDiagnosis(raw.primary_diagnosis || c.primary_diagnosis || c.diagnosis || raw.diagnosis_group || "Diagnosis not recorded", text);
  const icd10 = raw.icd10 || c.icd10 || "not recorded";
  const ctnm = raw.c_tnm || c.c_tnm || [raw.tnm_t, raw.tnm_n, raw.tnm_m].filter(Boolean).join(" ");
  const stageGroup = raw.stage_group || c.stage_group || "";
  const stageSummary = raw.stage_summary || c.stage_summary || "";
  const postOp = raw.yp_tnm || c.yp_tnm || raw.p_tnm || c.p_tnm || "";
  const stage = compactStage(ctnm, stageGroup, stageSummary);
  const plan = currentRtPlan(raw, c);
  return { diagnosis, icd10, stage, postOp, group, plan };
}

function diagnosisGroupLabel(value?: string | null): string {
  const v = String(value || "").toLowerCase();
  if (/breast|c50|her2|hr\+|er\+|pr\+|lumpectomy|mastectomy/.test(v)) return "Breast";
  if (/prostate|psa|gleason/.test(v)) return "Prostate";
  if (/rect|colon|anal|gi\b|gastro/.test(v)) return "GI";
  if (/lung|bronch/.test(v)) return "Thoracic";
  if (/brain|glioma|cns/.test(v)) return "CNS";
  return titleCase(String(value || "Diagnosis pending"));
}

function cleanDiagnosis(value: string, evidenceText: string): string {
  const explicitM0 = hasExplicitM0(evidenceText);
  const explicitM1 = hasExplicitM1(evidenceText);
  if (explicitM0 && !explicitM1) {
    return value.replace(/^\s*metastatic\s+/i, "").replace(/\bmetastatic\s+breast cancer\b/i, "breast cancer").trim();
  }
  return value.trim();
}

function compactStage(ctnm?: string | null, stageGroup?: string | null, stageSummary?: string | null): string {
  const c = String(ctnm || "").trim();
  const group = String(stageGroup || "").trim();
  const summary = String(stageSummary || "").trim();
  if (c && group && !summary.toLowerCase().includes(group.toLowerCase())) return `${c}, ${group}`;
  if (c && summary) {
    const withoutPostOp = summary.replace(/;?\s*post-op\s+p[^;]+/i, "").trim();
    return withoutPostOp && !withoutPostOp.toLowerCase().startsWith(c.toLowerCase()) ? `${c}, ${withoutPostOp}` : (withoutPostOp || c);
  }
  return c || group || summary || "not recorded";
}

function currentRtPlan(raw: any, c: any): string {
  const candidates = [
    c.current_rt_plan,
    c.rt_plan,
    c.radiotherapy_plan,
    c.planned_radiotherapy,
    raw.current_rt_plan,
    raw.rt_plan,
    valueFromText(c.notes_text, "plan:"),
    valueFromText(raw.next_action_text, "plan:"),
  ].filter(Boolean).map((x) => String(x).replace(/^rt plan:\s*/i, "").replace(/^plan:\s*/i, "").trim());
  if (candidates[0]) return candidates[0];
  const followUp = raw.follow_up_interval || c.follow_up_interval;
  if (followUp) return `Follow-up every ${String(followUp).replace(/^every\s+/i, "")}`;
  const status = String(raw.status_label || "");
  if (/follow[_\s-]*up/i.test(status)) return titleCase(status);
  return "not recorded";
}

type RTDisplay = { statusBadge: string; statusTone: Tone; historyBadge: string; historyTone: Tone; summary: string };

function radiotherapyDisplay(patient: Patient, clinical?: any): RTDisplay {
  const raw: any = (patient || {}) as any;
  const c = clinical || {};
  const dose = c.rt_dose || c.radiotherapy_dose || c.last_rt_dose || raw.rt_dose || raw.radiotherapy_dose || raw.last_rt_dose;
  const fractions = c.rt_fractions || c.fractions || c.last_rt_fractions || raw.rt_fractions || raw.fractions || raw.last_rt_fractions;
  const date = c.rt_date || c.radiotherapy_date || c.last_rt_end_date || raw.rt_date || raw.radiotherapy_date || raw.last_rt_end_date;
  const site = c.last_rt_site || raw.last_rt_site || "";
  const statusRaw = String(c.rt_status || c.radiotherapy_status || raw.rt_status || raw.radiotherapy_status || "").toLowerCase();
  const noPriorText = [dose, site, statusRaw, c.notes_text, raw.rt_summary].filter(Boolean).join(" ").toLowerCase();
  let statusBadge = rtStatusLabel(statusRaw);
  let historyBadge = "RT history unknown";
  let historyTone: Tone = "muted";
  if (/none|no prior|not received|never/.test(noPriorText) || (statusRaw === "pre-rt" && !dose && !fractions && !date)) {
    historyBadge = "No prior RT";
    historyTone = "ok";
  } else if (date && (dose || fractions || site)) {
    historyBadge = [site || "Prior RT", dose, fractions ? `${fractions} Fr#` : "", formatDateTime(String(date)).slice(0, 10)].filter(Boolean).join(" · ");
    historyTone = "warn";
  } else if (/prior rt|re.?irradiation|previous rt/.test(noPriorText)) {
    historyBadge = "Prior RT";
    historyTone = "warn";
  } else if (statusRaw === "completed" || statusRaw === "post-rt" || date) {
    historyBadge = "RT completed";
    historyTone = "ok";
  }
  if (statusBadge && statusBadge === historyBadge) statusBadge = "";
  return {
    statusBadge,
    statusTone: rtStatusTone(statusRaw),
    historyBadge,
    historyTone,
    summary: [statusBadge, historyBadge].filter(Boolean).join(" · "),
  };
}

function rtStatusLabel(status: string): string {
  const s = status.toLowerCase();
  if (["pre-rt", "pre rt", "planned", "rt_decision"].includes(s)) return "Pre-RT";
  if (["on-rt", "on rt", "active", "treating"].includes(s)) return "On RT";
  if (["completed", "post-rt", "post rt"].includes(s)) return "RT completed";
  if (["follow-up", "follow up", "surveillance"].includes(s)) return "Follow-up";
  if (s === "palliative") return "Palliative";
  if (s === "consult") return "Consult";
  return "";
}

function rtStatusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (s.includes("on")) return "info";
  if (s.includes("pre") || s.includes("planned") || s.includes("decision")) return "info";
  if (s.includes("completed") || s.includes("post")) return "ok";
  if (s.includes("palliative")) return "warn";
  return "muted";
}

function diagnosisSummary(patient: Patient, clinical?: any): string {
  const h = patientHeaderSummary(patient, clinical);
  const raw: any = (patient || {}) as any;
  const c = clinical || {};
  const evidence = [raw.stage_summary, raw.c_tnm, raw.p_tnm, raw.stage_group, raw.metastatic_site_codes, c.c_tnm, c.p_tnm, c.stage_group, c.metastatic_site_codes].filter(Boolean).join(" ");
  const metastaticPrefix = hasExplicitM1(evidence) && !hasExplicitM0(evidence) ? "Metastatic " : "";
  return `${metastaticPrefix}${h.diagnosis} ICD10: ${h.icd10}, Stage: ${h.stage}${h.postOp ? `, Post-op: ${h.postOp}` : ""}`;
}

function hasExplicitM0(text: string): boolean {
  return /\bc?m0\b|\byp?m0\b|non[-\s]?metastatic|stage\s*i\b|stage\s*ia\b|stage\s*ib\b|stage\s*ii\b|stage\s*iii\b/i.test(text);
}

function hasExplicitM1(text: string): boolean {
  const t = text.toLowerCase();
  if (/\bc?m1\b|\byp?m1\b/.test(t)) return true;
  if (/metastatic[_\s-]?site/.test(t) && !/not recorded|none|no prior/.test(t)) return true;
  return /distant metast|bone metast|osseous metast|liver metast|lung metast|brain metast|metastases documented/.test(t);
}

function metastaticCodesFromText(text: string): string {
  const codes: string[] = [];
  if (/osseous metast|bone metast|oss/.test(text)) codes.push("OSS");
  if (/lung metast|pulm/.test(text)) codes.push("PUL");
  if (/liver metast|hep/.test(text)) codes.push("HEP");
  if (/brain metast|cns/.test(text)) codes.push("BRA");
  return codes.join(", ");
}

function radiotherapySummary(patient: Patient, clinical?: any): string {
  return radiotherapyDisplay(patient, clinical).summary || "RT history unknown";
}

function nextVisitContentWithItemPatch(content: any, index: number, patch: Record<string, any>): any {
  const items = Array.isArray(content?.items) ? [...content.items] : [];
  items[index] = { ...(items[index] || {}), ...patch };
  return { ...(content || {}), items };
}

function nextVisitContentWithItemStatus(content: any, index: number, status: string): any {
  const stampKey = status === "converted_to_task" ? "converted_at" : `${status}_at`;
  return nextVisitContentWithItemPatch(content, index, { status, [stampKey]: new Date().toISOString() });
}

function nextVisitSummary(content: any): string {
  const items = Array.isArray(content?.items) ? content.items : [];
  const active = items.filter((item: any) => !["done", "resolved", "dismissed", "converted_to_task"].includes(String(item.status || "open").toLowerCase()));
  if (!active.length) return "No active next-visit checklist items.";
  return ["Next Visit Suggested Note", ...active.map((item: any) => {
    const detail = item.description ? ` — ${item.description}` : "";
    const urgency = item.urgency ? ` [${item.urgency}]` : "";
    return `- ${item.title}${urgency}${detail}`;
  })].join("\n");
}
